<script setup lang="ts">
/**
 * MY — Allotment of Shares (Cash) PreSet Panel (M32200).
 *
 * 11개 문서 패키지 — Variable Map: `Allotment_Shares_Cash_Variable_Map.md`.
 * SKILL.md `my-preset` 표준 (PreSetChangeOfficerPanel 답습) — props/emits/co-* CSS/AGM 디자인.
 *
 * Phase 1 (이 파일): 셸 + 회사/비서/이사/주주/날짜 공통 섹션 + Create Task skeleton.
 * Phase 2: 11 문서별 변수 입력 + Person Lookup + 분기 + handleCreateTask 실 호출.
 */
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted, type ComputedRef } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, OfficerRoleType } from '@/features/AI-documents/types/docGen.types';
import {
  buildAllotmentSharesVariables,
  detectAllotmentSharesDocFlags,
} from '../mappers/allotmentSharesMapper';
import type {
  CosecInput, DirectorInput, AllotteeInput, SubscriberInput, LodgerInput, GoverningDocumentType,
} from '../mappers/allotmentSharesMapper';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { useDateField } from '@/shared/composables/dateUtils';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import PreSetDateField from './PreSetDateField.vue';
import nationalitiesJson from '../../components/shared/data/MBRS/nationalities.json';
import occupationsJson from '../../components/shared/data/MBRS/occupations.json';

// MBRS 코드리스트 — Nationality / Occupation 콤보박스용 (라벨 문자열 배열).
const NATIONALITY_ITEMS = (nationalitiesJson as Array<{ value: string; label: string }>).map((n) => n.label);
const OCCUPATION_ITEMS = (occupationsJson as Array<{ value: string; label: string }>).map((o) => o.label);

interface PersonLookupContext {
  fieldGroup: 'cosec' | 'director' | 'allottee' | 'subscriber' | 'lodger' | 'bo';
  index?: number;
  defaultRole: OfficerRoleType | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + entity 자동 prefill skip(restoreState 가 채움) + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', context: PersonLookupContext): void;
  (e: 'request-address-lookup', context: { fieldGroup: string; index: number; addresses: any[]; subjectLabel?: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();

// ─── 문서 분기 플래그 ───
const docFlags = computed(() => detectAllotmentSharesDocFlags(props.selection.documents));

// ─── 진행/생성 상태 ───
const creating = ref(false);
const progress = ref('');

// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (로컬 creating/progress 는 버튼 :loading·인라인 에러용으로 유지)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

// ─── Entity / 회사 ───
const entityCd = computed(() => props.entityData?.entityCd || props.entityData?.icpCd || '');
const entityName = computed(() => props.entityData?.entityName || props.entityData?.companyName || '');
const regNo = computed(() => props.entityData?.registrationNo || '');
const articleNo = ref('');
const articleNoOptions = ref<string[]>([]);
const entityLoading = ref(false);

/** 입력 문자열에서 Article/Clause 번호만 추출.
 *   "Article 4(1)" → "4(1)" / "Clause 15" → "15" / 그 외는 첫 숫자 token. */
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

/** v-combobox model — 선택/입력값을 parseClauseNo 로 정규화해서 ref 에 저장 + 거버닝-타입 자동 감지. */
const constitutionClauseModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => {
    const s = val || '';
    constitutionClauseNo.value = parseClauseNo(s);
    // 필터(clauseOptionType)와 동일 분류기로 라디오 자동 감지 — 탭 튕김 방지.
    const detected = clauseOptionType(s);
    if (detected !== 'other') governingDocumentType.value = detected;
  },
});

// ── DCR 거버닝-문서 분기 (AOD/Change Address 동일) ──
const governingDocumentType = ref<GoverningDocumentType>('constitution');
watch(governingDocumentType, (t) => { if (t === 'third_schedule') constitutionClauseNo.value = ''; });

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  //   setter(clauseOptionType 위임)와 동일 분류 → 탭 튕김 없음.
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 Governing Document 라디오에 맞는 clause 옵션만 노출 (분류 불가 'other' 는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === governingDocumentType.value || ot === 'other';
  })
);

// Entity 에 정관/결의 근거가 등록돼 있으면 true → 라디오 + clause 잠금(직원 오선택 방지).
const entityClauseRegistered = ref(false);
// Governing Document 편집 잠금 — Entity 등록값(articleNo/clause)이 있으면 readonly.
// 잠금은 오직 Entity 등록 여부로만 결정한다 (new/edit 무관, 미등록이면 편집 가능).
const governingDocLocked = computed(() => entityClauseRegistered.value);

/** Entity 등록 Governing Document 텍스트 → 라디오 타입 + clause 번호 적용 + 잠금 플래그 set.
 *  우선순위: Third Schedule > Constitution > Articles. (AOD/Change Address applyRegisteredClause 동일) */
function applyRegisteredClause(regClause: string): boolean {
  const s = String(regClause || '').trim();
  if (!s) return false;
  entityClauseRegistered.value = true;
  if (/third\s*schedule/i.test(s)) {
    governingDocumentType.value = 'third_schedule';
    constitutionClauseNo.value = '';
  } else if (/constitution/i.test(s)) {
    governingDocumentType.value = 'constitution';
    constitutionClauseNo.value = parseClauseNo(s);
  } else if (/articles?\s+of\s+association/i.test(s)) {
    governingDocumentType.value = 'articles';
    constitutionClauseNo.value = parseClauseNo(s);
  } else {
    governingDocumentType.value = 'constitution';
    constitutionClauseNo.value = parseClauseNo(s);
  }
  return true;
}
// 이사 수 — directors[] 기준 자동 (1명=sole). DCR is_sole_director_company 분기.
const directorCount = computed<'single' | 'multiple'>(
  () => (directors.value.filter((d) => d.name?.trim()).length === 1 ? 'single' : 'multiple'),
);
// 멤버 수 (Authority to Issue Shares MWR) — 기존 멤버(allottees) 수로 자동 기본값(1명=sole),
//   사용자가 라디오를 직접 바꾸면 수동값 고정(memberCountManual). is_sole_member / is_multiple_member.
const memberCount = ref<'single' | 'multiple'>('multiple');
const memberCountManual = ref(false);
function onMemberCountChange(v: any) {
  memberCount.value = (v === 'single' ? 'single' : 'multiple');
  memberCountManual.value = true;
}
// 공인(common seal) 보유 — 패널 토글 (Increase of Paid-Up Capital). 기본 미보유.
const hasCommonSeal = ref(false);

const companyName = ref('');
const companyRegistrationNo = ref('');
const companyAddress = ref('');
const constitutionClauseNo = ref('');
const hasConstitution = ref(true);

// ─── 비서 (단일) ───
const cosec = ref<CosecInput>({
  name: '', email: '', address: '', licenseNo: '', nricNo: '',
  ssmCertNo: '', firm: '', companyName: '', phone: '',
});

// ─── 이사 (다수) ───
const directors = ref<DirectorInput[]>([]);

// ─── 주주 / 배정 대상자 (allottees) — 청약자(Subscriber)와 동일인이므로 단일 소스 ───
//   각 allottee 가 배정(Allotment) + 청약(Subscription) 정보를 함께 보유.
const allottees = ref<AllotteeInput[]>([]);

// 청약자(Subscriber) 목록은 allottees 에서 자동 파생 — 이름 있는 allottee = 청약자.
//   mapper / N장 복제 / skip 판정이 이 computed 를 사용 (입력은 Allottees 한 곳에서만).
const subscribers = computed<SubscriberInput[]>(() =>
  allottees.value
    .filter((a) => a.name?.trim())
    .map((a) => ({
      name: a.name,
      type: (a.type as SubscriberInput['type']) || 'individual',
      id: a.idNo || '',
      address: a.address || '',
      occupation: a.occupation,
      dob: a.dob,
      nationality: a.nationality,
      race: a.race,
      // Offered / Subscribed / Accepted — 입력값 우선, 없으면 Allotted Shares(a.shares) fallback.
      sharesOffered: a.sharesOffered ?? (Number(a.shares) || undefined),
      sharesSubscribed: a.sharesSubscribed ?? (Number(a.shares) || undefined),
      sharesAccepted: a.sharesAccepted ?? (Number(a.shares) || undefined),
      isPolitician: a.isPolitician, politicianDetails: a.politicianDetails,
      isPepConnected: a.isPepConnected, pepConnectionDetails: a.pepConnectionDetails,
      holdsGovtPosition: a.holdsGovtPosition, govtPositionDetails: a.govtPositionDetails,
      isTrustee: a.isTrustee,
      boFullName: a.boFullName, boRegistrationNo: a.boRegistrationNo, boNature: a.boNature,
      boPlace: a.boPlace, boRegisteredAddress: a.boRegisteredAddress, boBusinessAddress: a.boBusinessAddress,
      appendixRef: a.appendixRef,
    })),
);

// ─── Allotted Shares → Offered/Subscribed/Accepted 자동 채움 (사용자가 안 바꾼 필드만 따라감) ───
//   allottee.shares(=Allotted) 변경 시 3개 청약 수량 필드를 동일하게 갱신.
//   단, 직전 auto값과 다르게 사용자가 수정한 필드는 보존(override). prev 추적은 WeakMap.
const _shareSyncPrev = new WeakMap<object, number>();
watch(
  () => allottees.value.map((a) => Number(a.shares) || 0),
  () => {
    for (const a of allottees.value) {
      const cur = Number(a.shares) || 0;
      const prev = _shareSyncPrev.get(a);
      const follow = (v: any) => v == null || v === '' || v === prev; // 미입력 or 직전 auto값과 동일 → 자동 추종
      if (follow(a.sharesOffered)) a.sharesOffered = cur;
      if (follow(a.sharesSubscribed)) a.sharesSubscribed = cur;
      if (follow(a.sharesAccepted)) a.sharesAccepted = cur;
      _shareSyncPrev.set(a, cur);
    }
  },
  { immediate: true },
);

// ─── Lodger (Section 76 Secretary 제출인) ───
const lodger = ref<LodgerInput>({});

// ─── 금액 계산 헬퍼 (paidcal.txt 기준) ───
/** 빈값/공백/비숫자 → null, 그 외 유한수. (paidcal.txt 7 — empty 값 처리용) */
function toNum(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
/** RM 금액 — 소수점 2자리 반올림. (paidcal.txt 8 — currency precision) */
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const _ONES = [
  '', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
  'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
  'SEVENTEEN', 'EIGHTEEN', 'NINETEEN',
];
const _TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
const _SCALES = ['', ' THOUSAND', ' MILLION', ' BILLION', ' TRILLION'];

function _below1000(numIn: number): string {
  let num = numIn;
  let str = '';
  if (num >= 100) {
    str += `${_ONES[Math.floor(num / 100)]} HUNDRED`;
    num %= 100;
    if (num > 0) str += ' AND ';
  }
  if (num >= 20) {
    str += _TENS[Math.floor(num / 10)];
    if (num % 10 > 0) str += ` ${_ONES[num % 10]}`;
  } else if (num > 0) {
    str += _ONES[num];
  }
  return str.trim();
}

/** 정수 → 영문 단어 (최대 조 단위). */
function numberToWords(n: number): string {
  let remaining = Math.floor(Math.abs(n));
  if (remaining === 0) return 'ZERO';
  const groups: number[] = [];
  while (remaining > 0) {
    groups.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }
  let result = '';
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    result += `${_below1000(groups[i])}${_SCALES[i] || ''} `;
  }
  return result.trim();
}

/** RM 금액 → "RINGGIT MALAYSIA [WORDS] (AND [SEN] SEN) ONLY". (paidcal.txt 3) */
function formatRinggitWords(amount: number): string {
  const whole = Math.floor(amount);
  const sen = Math.round((amount - whole) * 100);
  let words = `RINGGIT MALAYSIA ${numberToWords(whole)}`;
  if (sen > 0) words += ` AND ${numberToWords(sen)} SEN`;
  return `${words} ONLY`;
}

// ─── 주식 정보 ───
//   Share Price 기본 RM 1 (par value 통상 RM1). 고객이 주식 수를 입력했는데 비어있으면 1 로 재설정.
const sharePrice = ref<number | string>(1);
// 1. Total New Shares = Σ allottees[].shares (paidcal.txt 1)
const totalNewShares = computed(() =>
  allottees.value.reduce((sum, a) => sum + (Number(a.shares) || 0), 0),
);
watch(totalNewShares, (n) => {
  if (n > 0 && (sharePrice.value === '' || sharePrice.value === null || sharePrice.value === undefined)) {
    sharePrice.value = 1;
  }
});
// 2. Total Amount = totalNewShares × sharePrice. shares 또는 price 비면 '' (paidcal.txt 2/7)
const totalAmount = computed<number | ''>(() => {
  const price = toNum(sharePrice.value);
  const shares = totalNewShares.value;
  if (price === null || !shares) return '';
  return round2(shares * price);
});
// 3. Amount in Words — totalAmount 자동 변환. totalAmount 없으면 '' (paidcal.txt 3/7)
const amountInWords = computed<string>(() =>
  typeof totalAmount.value === 'number' ? formatRinggitWords(totalAmount.value) : '',
);
// 4. Paid-Up Capital Before — entity register 에서 자동 로드 (watch), 사용자 수정 가능
const paidUpCapitalBefore = ref<number | string>('');
// Section 78 — Entity Register 의 기존 발행 주식 수 (이번 배정 전). Total Current / Accumulated 계산용.
const currentIssuedShares = ref<number>(0);
// Section 78 합계 (화면 표시 + mapper 전달)
const totalAdditionalShares = computed(() => Number(totalNewShares.value) || 0);
const totalAccumulatedShares = computed(() => (Number(currentIssuedShares.value) || 0) + totalAdditionalShares.value);
// Section 78 Share Details — allottee 를 class 별로 묶어 자동 생성 (화면 표시용)
const shareDetailRows = computed(() => {
  const price = Number(sharePrice.value) || 0;
  const byClass = new Map<string, number>();
  for (const a of allottees.value) {
    if (!a.name?.trim()) continue;
    const cls = (a.shareType || 'Ordinary').trim() || 'Ordinary';
    byClass.set(cls, (byClass.get(cls) || 0) + (Number(a.shares) || 0));
  }
  return Array.from(byClass.entries()).map(([cls, num]) => ({
    type: cls, number: num, price, totalIssued: num * price,
  }));
});
// 5. Paid-Up Capital After = before + totalAmount. before 비면 '' (paidcal.txt 5/7)
const paidUpCapitalAfter = computed<number | ''>(() => {
  const before = toNum(paidUpCapitalBefore.value);
  if (before === null) return '';
  const amt = typeof totalAmount.value === 'number' ? totalAmount.value : 0;
  return round2(before + amt);
});
// 8. 2-decimal 표시용 (paidcal.txt 8)
const totalAmountDisplay = computed(() =>
  typeof totalAmount.value === 'number' ? totalAmount.value.toFixed(2) : '',
);
const paidUpCapitalAfterDisplay = computed(() =>
  typeof paidUpCapitalAfter.value === 'number' ? paidUpCapitalAfter.value.toFixed(2) : '',
);

// ─── 날짜군 ───
//   UX 결정(추천안 1): 고객 입력은 2개만 받고 나머지는 자동 파생.
//     · Resolution Date → DCR / Circulation / Letter / Signature
//     · Allotment Date  → Event
//     · Deadline Date   = Resolution Date + 28일
//   날짜 입력 UI 는 Incorporation Post 패턴(useDateField) 답습 — 초록 달력 메뉴 + 직접 텍스트 입력(blur 파싱).
const resolutionDateField = useDateField();
const resolutionDate = resolutionDateField.dateValue;              // 내부 저장: YYYY-MM-DD
const resolutionDateMenu = resolutionDateField.dateMenu;
const resolutionDatePickerValue = resolutionDateField.datePickerValue;
const resolutionDatePickerProps = resolutionDateField.datePickerProps;
const resolutionLocalText = resolutionDateField.localText;
const resolutionOnBlur = resolutionDateField.onDateTextBlur;
const resolutionDatePlaceholder = resolutionDateField.datePlaceholder;

const allotmentDateField = useDateField();
const allotmentDate = allotmentDateField.dateValue;
const allotmentDateMenu = allotmentDateField.dateMenu;
const allotmentDatePickerValue = allotmentDateField.datePickerValue;
const allotmentDatePickerProps = allotmentDateField.datePickerProps;
const allotmentLocalText = allotmentDateField.localText;
const allotmentOnBlur = allotmentDateField.onDateTextBlur;
const allotmentDatePlaceholder = allotmentDateField.datePlaceholder;

// Section 78 "between the [[period_start_date]] and [[period_end_date]]" — 여러 배정 건의 기간 입력.
//   비우면 mapper 가 배정일(allotment_date)로 기본 설정. 저장은 YYYY-MM-DD.
const periodStartDate = ref('');
const periodEndDate = ref('');

// ─── 필드 Skip (AOD preset 패턴) ───
//   고객이 날짜를 나중에 채울 수 있으므로 X 로 skip → 해당 날짜(및 파생)를 blank 로 lodge.
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(field: string): boolean { return skippedFields.value.has(field); }
function toggleFieldSkip(field: string, skip: boolean) {
  if (skip) skippedFields.value.add(field);
  else skippedFields.value.delete(field);
}
function valOrBlank(field: string, value: string): string {
  return isFieldSkipped(field) ? '' : value;
}

// skip-aware 소스값 — 파생 날짜의 base 로 사용 (skip 시 파생도 blank 로 따라감).
const resolutionEffective = computed(() => valOrBlank('resolutionDate', resolutionDate.value));
const allotmentEffective = computed(() => valOrBlank('allotmentDate', allotmentDate.value));

/** "YYYY-MM-DD" 에 days 를 더해 "YYYY-MM-DD" 반환 (로컬 기준, 빈값/무효 → ''). */
function addDays(dateStr: string, days: number): string {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// 파생 날짜 base(auto) — skip-aware 소스에서 계산 (소스 skip 시 파생도 자동 blank).
const dcrAuto = computed(() => resolutionEffective.value);
const circulationAuto = computed(() => resolutionEffective.value);
const letterAuto = computed(() => resolutionEffective.value);
const signatureAuto = computed(() => resolutionEffective.value);
const eventAuto = computed(() => allotmentEffective.value);
const deadlineAuto = computed(() => addDays(resolutionEffective.value, 28));

/**
 * 파생 날짜 1개 — auto 값을 기본 표시하되 사용자가 직접 입력/달력으로 override 가능.
 *   · override 안 했으면 auto 를 따라 자동 갱신
 *   · 직접 입력/달력 선택 시 override 고정 (`auto` 링크로 원복)
 *   · 빈칸으로 지우면 다시 auto fallback
 * useDateField 답습(초록 달력 + blur 텍스트 파싱). reactive 로 감싸 템플릿에서 .value 없이 바인딩.
 */
function createDerivedDate(autoRef: ComputedRef<string>) {
  const f = useDateField();
  const overridden = ref(false);
  watch(autoRef, (v) => { if (!overridden.value) f.dateValue.value = v; }, { immediate: true });
  const effective = computed(() => f.dateValue.value || autoRef.value);
  const onBlur = (val?: string) => {
    f.onDateTextBlur(val);
    overridden.value = !!f.dateValue.value && f.dateValue.value !== autoRef.value;
  };
  const onPick = () => {
    f.dateMenu.value = false;
    overridden.value = f.dateValue.value !== autoRef.value;
  };
  const reset = () => { overridden.value = false; f.dateValue.value = autoRef.value; };
  return reactive({
    localText: f.localText,
    placeholder: f.datePlaceholder,
    menu: f.dateMenu,
    pickerValue: f.datePickerValue,
    pickerProps: f.datePickerProps,
    effective, overridden, onBlur, onPick, reset,
  });
}

const dcrD = createDerivedDate(dcrAuto);
const circulationD = createDerivedDate(circulationAuto);
const letterD = createDerivedDate(letterAuto);
const signatureD = createDerivedDate(signatureAuto);
const eventD = createDerivedDate(eventAuto);
const deadlineD = createDerivedDate(deadlineAuto);

const derivedDates = [
  { key: 'dcr', label: 'DCR Date', d: dcrD },
  { key: 'circulation', label: 'Circulation Date', d: circulationD },
  { key: 'letter', label: 'Letter Date', d: letterD },
  { key: 'signature', label: 'Signature Date', d: signatureD },
  { key: 'deadline', label: 'Deadline (+28d)', d: deadlineD },
  { key: 'event', label: 'Event Date', d: eventD },
];

// build / mapper 로 보낼 최종 effective 값 (override 우선, 없으면 auto). 소스 skip 시 auto='' → blank.
const dcrDate = computed(() => dcrD.effective);
const circulationDate = computed(() => circulationD.effective);
const letterDate = computed(() => letterD.effective);
const signatureDate = computed(() => signatureD.effective);
const eventDate = computed(() => eventD.effective);
const deadlineDate = computed(() => deadlineD.effective);

// ─── 날짜 일괄 Skip ───
//   2개 소스(Resolution/Allotment)를 한 번에 skip/restore. skip 시 파생 override 도 해제해 모두 blank.
const DATE_SKIP_KEYS = ['resolutionDate', 'allotmentDate'];
const allDatesSkipped = computed(() => DATE_SKIP_KEYS.every((k) => isFieldSkipped(k)));
function toggleAllDatesSkip() {
  const skip = !allDatesSkipped.value;
  DATE_SKIP_KEYS.forEach((k) => toggleFieldSkip(k, skip));
  if (skip) derivedDates.forEach((it) => it.d.reset()); // 파생 override 해제 → auto('') 따라 blank
}

// ─── 조건 ───
const grantOfRights = ref(false);
const hasConditions = ref(false);
const conditionsText = ref('');
// Multiple Members(Waiver) — 기존 주주/배정자 2명 이상이면 자동 true (수동 체크 제거).
const multipleMembers = computed(() => allottees.value.filter((a) => a.name?.trim()).length > 1);
// memberCount 자동 기본값 — 사용자가 직접 안 바꿨으면 멤버(allottees) 수로 sole/multiple 설정.
const namedMemberCount = computed(() => allottees.value.filter((a) => a.name?.trim()).length);
watch(namedMemberCount, (n) => {
  if (memberCountManual.value) return;
  memberCount.value = n === 1 ? 'single' : 'multiple';
}, { immediate: true });
const submissionReference = ref('');

// PEP 선언 도움말 — 체크박스 옆 ❓ 호버 툴팁(짧고 고객 친화적인 설명 + 예시).
const PEP_GUIDE = [
  {
    desc: 'Tick if the subscriber is currently, or has recently been, a politician.',
    examples: ['Member of Parliament (MP)', 'Minister', 'Mayor', 'State Assembly Member', 'Political party leader'],
  },
  {
    desc: 'Tick if the subscriber is closely related to, or associated with, a politician.',
    examples: ['Spouse of a politician', 'Child or parent of a politician', 'Business partner of a politician', 'Person acting on behalf of a politician'],
  },
  {
    desc: 'Tick if the subscriber holds a senior position in a government body.',
    examples: ['Government department director', 'Judge or prosecutor', 'Senior military officer', 'CEO or director of a government-linked company (GLC)', 'Ambassador or senior diplomat'],
  },
] as const;

// ─── 폼 노출 조건 (taskTitle + entity 둘 다 + prefill 완료) ───
const formReady = computed(() => !!props.taskTitle && !!entityCd.value && !entityLoading.value);

// ─── Entity prefill watch ───
//   entity 선택 시 회사 정보 + 비서 / 이사 / 멤버 자동 채움.
watch(
  () => entityCd.value,
  async (cd) => {
    if (!cd) {
      entityLoading.value = false;
      return;
    }
    // Governing Document(정관 결의 근거)는 **entity-authoritative** → create/edit 모두 fetch + 잠금 적용.
    //   (Edit 모드라도 entity 의 등록 정관이 우선이라 restoreState 의 stale 값보다 이게 맞다.)
    try {
      const govAddr = await fetchEntityAddresses(cd);
      if (govAddr) {
        articleNo.value = String(govAddr.articleNo || '').trim();
        const regClause = String(govAddr.clauseNo || govAddr.articleNo || '').trim();
        if (regClause) applyRegisteredClause(regClause);
        else entityClauseRegistered.value = false;
        // company 주소/이름은 create 모드에서만 prefill (edit 는 restoreState 가 채움).
        if (!editMode.value) {
          if (!companyName.value && govAddr.companyName) companyName.value = govAddr.companyName;
          if (!companyRegistrationNo.value && govAddr.regNo) companyRegistrationNo.value = govAddr.regNo;
          if (!companyAddress.value && govAddr.registered) companyAddress.value = String(govAddr.registered);
        }
      }
    } catch (err) {
      console.warn('[AllotmentShares] governing-document fetch failed:', err);
    }

    // Edit 모드: 저장된 입력을 restoreState 가 채우므로 나머지 자동 prefill(멤버/비서/이사 덮어쓰기) skip.
    if (editMode.value) {
      entityLoading.value = false;
      return;
    }
    entityLoading.value = true;
    progress.value = 'Loading entity data...';
    try {
      // 1) 즉시 적용 — entityData 가 제공하는 기본값
      if (props.entityData?.companyName || props.entityData?.entityName) {
        companyName.value = props.entityData.companyName || props.entityData.entityName || '';
      }
      if (props.entityData?.registrationNo) {
        companyRegistrationNo.value = props.entityData.registrationNo;
      }

      // 3) Constitution Clause No 옵션 목록 — ChangeOfficer 와 동일한 경량 엔드포인트.
      //   Entity 에 articleNoOptions 가 있으면 콤보박스 옵션으로 표시. 없으면 사용자가 직접 입력 (clearable text).
      try {
        const res = await fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' });
        const json = await res.json();
        const opts =
          json?.articleNoOptions ||
          json?.data?.resolution?.articleNoOptions ||
          json?.resolution?.articleNoOptions ||
          [];
        articleNoOptions.value = Array.isArray(opts)
          ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
          : [];
      } catch (err) {
        console.warn('[AllotmentShares] resolution-options fetch failed:', err);
        articleNoOptions.value = [];
      }

      // 4) Secretary 자동 바인딩 — 첫 번째 비서 → cosec.
      //   주소는 list(getSecretaries)가 아니라 챗봇과 동일한 officer detail(getOfficerDetail)에서
      //   가져와야 2개 주소(Residential/Service)가 정상 포맷으로 들어온다. (list 는 축약/단일 케이스)
      try {
        const secRes: any = await docGenApi.getSecretaries(cd);
        const secs = secRes?.data?.secretaries || secRes?.data || [];
        if (Array.isArray(secs) && secs.length > 0 && !cosec.value.name) {
          const s = secs[0];
          let detail: any = null;
          const officerIdx = Number(s.officerIdx ?? s.OFFICER_IDX);
          if (officerIdx) {
            try {
              const detRes: any = await docGenApi.getOfficerDetail(cd, officerIdx);
              detail = detRes?.data || detRes || null;
            } catch (e) {
              console.warn('[AllotmentShares] secretary detail fetch failed:', e);
            }
          }
          // detail 우선(license/cert 포함), 실패 시 list 데이터로 폴백.
          let cosecDetail: any = detail || {
            contactCd: s.contactCd || s.contactCode || '',
            name: s.name || s.contactName || '',
            licenseNumber: s.licenseNumber || '',
            contactRegNo: s.contactRegNo || '',
            mobileNo: s.mobileNo || '',
            contactEmail: s.contactEmail || '',
            ssmPractisingCertificateNumber: s.ssmPractisingCertificateNumber || '',
            addresses: Array.isArray(s.addresses) ? s.addresses : [],
            primaryAddress: s.primaryAddress || '',
          };
          // 주소 보강 — getOfficerDetail 은 inline 컬럼이라 신스키마(L_CONTACT_ADDRESS) 주소가 누락됨.
          //   챗봇/lookup 과 동일하게 getContactDetail(L_CONTACT_ADDRESS/BRANCH, addresses[])로 덮어쓴다.
          const secCc = String(
            cosecDetail.contactCd || cosecDetail.CONTACT_CD
            || s.contactCd || s.contactCode || s.CONTACT_CD || '',
          ).trim();
          if (secCc) {
            try {
              const cdRes: any = await docGenApi.getContactDetail(cd, secCc);
              const cdDet = cdRes?.data || cdRes;
              const addrs = Array.isArray(cdDet?.addresses) ? cdDet.addresses : [];
              if (addrs.length > 0) cosecDetail = { ...cosecDetail, addresses: addrs };
            } catch (e) {
              console.warn('[AllotmentShares] cosec getContactDetail addresses failed:', e);
            }
          }
          applyDetailToCosec(cosecDetail);
        }
      } catch (err) {
        console.warn('[AllotmentShares] getSecretaries failed:', err);
      }

      // 5) Directors 자동 바인딩 — Entity 의 모든 이사를 directors[] 에 prefill.
      //   incorporation post 패턴. 사용자가 +Add Director / × 로 추가/삭제 가능.
      try {
        const dirRes: any = await docGenApi.getDirectors(cd);
        const dirs = dirRes?.data?.directors || dirRes?.data || [];
        if (Array.isArray(dirs) && dirs.length > 0 && directors.value.length === 0) {
          directors.value = dirs.map((d: any): DirectorInput => ({
            contactCd: d.contactCd || d.contactCode || '',
            name: d.name || d.contactName || '',
            email: d.contactEmail || '',
            nricNo: d.contactRegNo || '',
            dateOfAppointment: d.appointmentDate || '',
          }));
        }
      } catch (err) {
        console.warn('[AllotmentShares] getDirectors failed:', err);
      }

      // 6) Members (= 기존 주주) → allottees prefill.
      //   ⚠ Allotment 의 "shares" 칸 의미는 **이번 task 에서 발행할 수량** — 기존 보유와 다름.
      //   사용자 편의로 기존 멤버 이름/contactCd/주소는 prefill 하되 shares 는 0 으로 시작.
      //   사용자가 발행할 멤버는 shares 입력, 발행 안 할 멤버는 × 로 제거.
      try {
        const memRes: any = await docGenApi.getMembers(cd);
        const mems = memRes?.data?.members || memRes?.data?.shares || memRes?.data || [];
        if (Array.isArray(mems) && mems.length > 0 && allottees.value.length === 0) {
          // contactCd 기준 dedupe — 같은 사람이 share class 별 여러 row 일 수 있음.
          const seen = new Set<string>();
          const dedup: any[] = [];
          for (const m of mems) {
            const cc = String(m.contactCd || m.contactCode || '').trim();
            const key = cc || `name:${String(m.name || m.contactName || '').toUpperCase()}`;
            if (seen.has(key)) continue;
            seen.add(key);
            dedup.push(m);
          }
          allottees.value = dedup.map((m: any): AllotteeInput => {
            const addrs = Array.isArray(m.addresses) ? m.addresses : [];
            const picked = pickServiceFirstAddress(addrs);
            // 개인/법인 — member 의 CATEGORY_CD (B09001 Corporate / B09002 Individual)
            const cat = String(m.categoryCd || m.CATEGORY_CD || '').trim().toUpperCase();
            const type: AllotteeInput['type'] =
              cat === 'B09001' ? 'corporate' : cat === 'B09002' ? 'individual' : 'individual';
            return {
              contactCd: m.contactCd || m.contactCode || '',
              name: m.name || m.contactName || '',
              address: picked.value || m.primaryAddress || '',
              idNo: m.contactRegNo || '',
              shares: 0, // 이번 task 발행 수량 — 사용자 입력
              shareType: 'Ordinary',
              type,
              nationality: m.nationalityName || m.nationality || '',
              dob: m.dateOfBirth || '',
              occupation: m.occupation || m.businessOccupation || '',
              race: m.race || '',
              addresses: addrs,
              addressSelectedIdx: picked.idx,
            };
          });
        }
      } catch (err) {
        console.warn('[AllotmentShares] getMembers failed:', err);
      }

      // 7) Paid-Up Capital Before 자동 로드 — MY Entity Shares 의 paidUpCapital 합계 (paidcal.txt 4).
      //   ⚠ apiClient(entityApi.getEntityShares → /entity/shares/:id)는 SG/글로벌 엔드포인트라 MY 엔티티에
      //     401/403 을 반환하고, 이 URL 은 client.ts 인터셉터 억제 목록에 없어 전역 로그아웃을 유발한다.
      //   → 같은 watch 의 fetchEntityAddresses / resolution-options 와 동일하게 raw fetch 로 MY 전용 경로 호출
      //     (인터셉터 우회 — best-effort prefill 이 세션을 내리면 안 됨). 사용자가 직접 수정 가능.
      try {
        {
          const shRes = await fetch(`/api/entity_my/shares/${encodeURIComponent(cd)}`, { credentials: 'include' });
          if (!shRes.ok) {
            console.warn('[AllotmentShares] entity_my/shares not ok — paid-up/current-shares auto-load skipped:', shRes.status);
          } else {
            const shJson: any = await shRes.json();
            // sendSuccessResponse 스프레드 → body = { success, data: { shares } }. (드물게 한 겹 더 중첩될 수 있어 둘 다 대응)
            const shData = shJson?.data?.shares ? shJson.data : (shJson?.data?.data ?? shJson?.data ?? shJson ?? {});
            const shares = Array.isArray(shData?.shares) ? shData.shares : [];
            // Section 78 — 기존 발행 주식 수(Total Current) 항상 캡처.
            currentIssuedShares.value = shares.reduce(
              (acc: number, s: any) => acc + (Number(s.numberOfShares ?? s.acqNumberOfShares ?? 0) || 0),
              0,
            );
            const sum = shares.reduce(
              (acc: number, s: any) =>
                acc + (Number(s.paidUpCapital ?? s.paidUpShareCapital ?? s.acqPaidUpShareCapital ?? 0) || 0),
              0,
            );
            console.info('[AllotmentShares] entity shares auto-load', { entityCd: cd, shareRows: shares.length, currentShares: currentIssuedShares.value, paidUpSum: sum });
            // Paid-Up Before — 비어있을 때만 자동 채움(0 포함, register row 있을 때).
            if (!paidUpCapitalBefore.value && shares.length > 0) paidUpCapitalBefore.value = round2(sum);
          }
        }
      } catch (err) {
        console.warn('[AllotmentShares] entity_my/shares (paid-up before) failed:', err);
      }

      // TODO Phase 2: autoBindOfficers (directors / cosec / existing members) — incorporation Post 패턴 참조
    } finally {
      entityLoading.value = false;
      progress.value = '';
    }
  },
  { immediate: true },
);

// ─── Person Lookup — selectedPerson 자동 채움 ───
//   chat 에서 사람 선택 → 풀 detail (`GET /api/Contact/:cc`) 받아 fieldGroup 별 모든 필드 prefill.
//   ChangeOfficer 패턴 답습.

/** 주소 후보 배열에서 service 우선 → residential → business → 첫 valid entry 선택 (incorporation post 와 다름 — 사용자 요청: service 기본). */
function pickServiceFirstAddress(addresses: any[]): { value: string; idx: number } {
  if (!Array.isArray(addresses) || !addresses.length) return { value: '', idx: -1 };
  const norm = (t: any) => String(t || '').trim().toLowerCase();
  const svcIdx = addresses.findIndex((a) => norm(a?.type) === 'service' && a?.value);
  if (svcIdx >= 0) return { value: String(addresses[svcIdx].value), idx: svcIdx };
  const resIdx = addresses.findIndex((a) => norm(a?.type) === 'residential' && a?.value);
  if (resIdx >= 0) return { value: String(addresses[resIdx].value), idx: resIdx };
  const bizIdx = addresses.findIndex((a) => norm(a?.type) === 'business' && a?.value);
  if (bizIdx >= 0) return { value: String(addresses[bizIdx].value), idx: bizIdx };
  const firstIdx = addresses.findIndex((a) => a?.value);
  return firstIdx >= 0
    ? { value: String(addresses[firstIdx].value), idx: firstIdx }
    : { value: '', idx: -1 };
}

/** Address chip label — Residential / Service / Business / Branch N. */
function addressSuggestionLabel(addr: any, idx: number, allAddrs: any[] | undefined): string {
  if (!allAddrs) return addr?.label || addr?.type || 'Address';
  if (addr?.type === 'branch') {
    const branchNo = allAddrs.slice(0, idx + 1).filter((a) => a?.type === 'branch').length;
    return `Branch ${branchNo}`;
  }
  if (addr?.label) return String(addr.label);
  const type = String(addr?.type || '').trim();
  return type ? type.charAt(0).toUpperCase() + type.slice(1) + ' Address' : 'Address';
}

/** 토큰 Title Case — 숫자/우편번호 등 글자 없는 토큰은 원본 유지. */
function _titleCaseToken(w: string): string {
  if (!w || !/[a-z]/i.test(w)) return w;
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}
/** 주소 문자열 Title Case 정규화 — backend addressResolver 의 normalizeDisplayString 과 동일 규칙
 *   (공백/슬래시/콤마/하이픈 구분자 보존). DB 원본이 전부 대문자라도 화면 표기를 통일. */
function normalizeAddrCase(s: string): string {
  if (!s) return '';
  return s
    .replace(/&amp;/gi, 'and').replace(/&/g, 'and')
    .replace(/\s+/g, ' ').trim()
    .split(/(\s+|\/|,|-)/)
    .map((p) => (/^[\s/,\-]+$/.test(p) ? p : _titleCaseToken(p)))
    .join('');
}

/** 날짜 문자열에서 시간 제거 → "YYYY-MM-DD" (예: "1982-11-01 12:00:00.0" → "1982-11-01"). */
function dateOnly(v: any): string {
  const s = String(v ?? '').trim();
  const m = s.match(/\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : (s.split(/[ T]/)[0] || '');
}

/** 주소 객체(JSON_DATA) → 단일 string 으로 평탄화 (street01..03, city, state, postcode, country 순). */
function flattenAddrObj(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  const parts = [
    obj.street01, obj.street02, obj.street03,
    obj.city, obj.state, obj.post_code || obj.postCode || obj.postcode,
    obj.country,
  ]
    .map((s) => String(s ?? '').trim())
    // raw baseCode(B1234…) 및 UUID(주소 컬럼에 잘못 들어간 식별자) 제외 — addressResolver formatAddressLine 과 동일 규칙.
    .filter((s) => s
      && !/^B\d{4,}$/i.test(s)
      && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s));
  // addressResolver 표준과 동일하게 Title Case 통일.
  return normalizeAddrCase(parts.join(', '));
}

function parseJsonField(raw: any): any | null {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(String(raw)); } catch { return null; }
}

/** Contact API 응답을 일관 형태로 normalize — snake/camel/대문자 key 모두 대응.
 *   주소는 ChangeOfficer 패턴 답습 — RESIDENTIAL_ADDR_JSON_DATA / SERVICE_ADDR_JSON_DATA / raw string /
 *   addresses[] 배열 등 여러 source 통합. 결과로 addresses[] 빌드해 chip 으로 사용. */
function normalizePersonDetail(raw: any): Record<string, any> {
  if (!raw || typeof raw !== 'object') return {};
  const d = raw;
  const profileJson = (() => {
    const rawJson = d.JSON_DATA ?? d.profileJson ?? d.profile_json ?? d.contactProfileJson;
    if (!rawJson) return {};
    if (typeof rawJson === 'string') {
      try { return JSON.parse(rawJson) || {}; } catch { return {}; }
    }
    return typeof rawJson === 'object' ? rawJson : {};
  })();

  // 주소 후보 배열 빌드 — 여러 source 통합.
  //   1) 기존 addresses[] 가 이미 있으면 그대로 사용 (chat widget 이 미리 정리한 케이스).
  //   2) 없으면 RESIDENTIAL_ADDR_JSON_DATA / SERVICE_ADDR_JSON_DATA + raw residentialAddress/serviceAddress
  //      에서 빌드. type 별로 한 entry 씩.
  let addresses: Array<{ type: string; value: string; label?: string }> = [];
  if (Array.isArray(d.addresses) && d.addresses.length > 0) {
    addresses = d.addresses
      .map((a: any) => ({ type: String(a?.type || ''), value: normalizeAddrCase(String(a?.value || '')), label: a?.label }))
      .filter((a: { value: string }) => a.value);
  } else if (Array.isArray(d.ADDRESSES) && d.ADDRESSES.length > 0) {
    addresses = d.ADDRESSES
      .map((a: any) => ({ type: String(a?.type || ''), value: normalizeAddrCase(String(a?.value || '')), label: a?.label }))
      .filter((a: { value: string }) => a.value);
  } else {
    // Service
    const svcJson = parseJsonField(d.SERVICE_ADDR_JSON_DATA || d.serviceAddrJsonData || d.service_addr_json_data);
    const svcStr = flattenAddrObj(svcJson)
      || (typeof d.serviceAddress === 'string' ? d.serviceAddress : '')
      || (typeof d.businessAddress === 'string' ? d.businessAddress : '')
      || flattenAddrObj(d.alternateAddress);
    if (svcStr) addresses.push({ type: 'service', value: String(svcStr).trim(), label: 'Service Address' });
    // Residential
    const resJson = parseJsonField(d.RESIDENTIAL_ADDR_JSON_DATA || d.residentialAddrJsonData || d.residential_addr_json_data);
    const resStr = flattenAddrObj(resJson)
      || (typeof d.residentialAddress === 'string' ? d.residentialAddress : '')
      || (typeof d.homeAddress === 'string' ? d.homeAddress : '')
      || (typeof d.primaryAddress === 'string' ? d.primaryAddress : '')
      || flattenAddrObj(d.registeredAddress || d.residential_address);
    if (resStr) addresses.push({ type: 'residential', value: String(resStr).trim(), label: 'Residential Address' });
    // Business (있을 때만)
    const bizJson = parseJsonField(d.BUSINESS_ADDR_JSON_DATA || d.businessAddrJsonData);
    const bizStr = flattenAddrObj(bizJson)
      || (typeof d.businessAddress === 'string' && !svcStr ? d.businessAddress : '');
    if (bizStr) addresses.push({ type: 'business', value: String(bizStr).trim(), label: 'Business Address' });
  }
  // dedupe — 같은 value 는 한 번만
  const seen = new Set<string>();
  addresses = addresses.filter((a) => {
    const k = a.value.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const picked = pickServiceFirstAddress(addresses);
  const fallbackAddr = !picked.value
    ? (d.primaryAddress || d.PRIMARY_ADDRESS || d.residentialAddress || d.serviceAddress || '')
    : picked.value;
  return {
    name: d.fullName || d.CONTACT_NAME || d.contactName || d.name || '',
    contactCd: d.contactCd || d.CONTACT_CD || '',
    nricNo: d.contactRegNo || d.CONTACT_REG_NO || d.nricNo || d.idNo || '',
    email: d.contactEmail || d.CONTACT_EMAIL || d.email || '',
    phone: d.mobileNo || d.MOBILE_NO || d.phone || '',
    address: String(fallbackAddr || ''),
    addresses,
    addressSelectedIdx: picked.idx,
    // getOfficerDetail(MY register-detail)은 license 를 license_number/SecretaryLicenseNo/SECRETARY_LICENSE_NO 로 줌 — 변형 키 모두 시도.
    licenseNo: d.licenseNumber || d.license_number || d.SecretaryLicenseNo || d.SECRETARY_LICENSE_NO || d.LICENSE
      || profileJson.license_number || profileJson.licenseNumber || '',
    ssmCertNo:
      d.ssmPractisingCertificateNumber || d.SecretaryCertificateNo || d.SECRETARY_CERTIFICATE_NO
      || profileJson.SecretaryCertificateNo || profileJson.secretaryCertificateNo || profileJson.ssmCertNo || '',
    occupation: d.businessOccupation || profileJson.Occupation || profileJson.occupation || '',
    nationality: d.nationality || d.NATIONALITY_NAME || d.nationalityName || '',
    dateOfBirth: dateOnly(d.dateOfBirth || d.DATE_OF_BIRTH || ''),
    gender: profileJson.gender || d.gender || '',
    race: profileJson.race || d.race || '',
    // 개인/법인 구분 — L_GLOBAL_CONTACT.CATEGORY_CD (B09001 Corporate / B09002 Individual)
    categoryCd: d.CATEGORY_CD || d.categoryCd || d.category_cd || d.category || '',
  };
}

/** cosec 에 detail 적용 */
function applyDetailToCosec(detail: any) {
  const n = normalizePersonDetail(detail);
  cosec.value.contactCd = n.contactCd;
  if (n.name) cosec.value.name = n.name;
  if (n.nricNo) cosec.value.nricNo = n.nricNo;
  if (n.email) cosec.value.email = n.email;
  if (n.phone) cosec.value.phone = n.phone;
  if (n.address) cosec.value.address = n.address;
  if (n.licenseNo) cosec.value.licenseNo = n.licenseNo;
  if (n.ssmCertNo) cosec.value.ssmCertNo = n.ssmCertNo;
  cosec.value.addresses = n.addresses || [];
  cosec.value.addressSelectedIdx = n.addressSelectedIdx ?? -1;
}

/** director[i] 에 detail 적용 */
function applyDetailToDirector(index: number, detail: any) {
  const n = normalizePersonDetail(detail);
  const target = directors.value[index];
  if (!target) return;
  target.contactCd = n.contactCd;
  if (n.name) target.name = n.name;
  if (n.nricNo) target.nricNo = n.nricNo;
  if (n.email) target.email = n.email;
}

/** allottee[i] 에 detail 적용 — 배정 + 청약(동일인) 정보 한 번에 채움. 개인/법인은 CATEGORY_CD 로 자동 판별. */
function applyDetailToAllottee(index: number, detail: any) {
  const n = normalizePersonDetail(detail);
  const target = allottees.value[index];
  if (!target) return;
  target.contactCd = n.contactCd;
  if (n.name) target.name = n.name;
  if (n.address) target.address = n.address;
  if (n.nricNo) target.idNo = n.nricNo;
  target.addresses = n.addresses || [];
  target.addressSelectedIdx = n.addressSelectedIdx ?? -1;
  // 청약자 상세 (동일인) 자동 채움
  if (n.nationality) target.nationality = n.nationality;
  if (n.race) target.race = n.race;
  if (n.occupation) target.occupation = n.occupation;
  if (n.dateOfBirth) target.dob = n.dateOfBirth;
  // 개인/법인 — B09001 Corporate / B09002 Individual
  const cat = String(n.categoryCd || '').trim().toUpperCase();
  if (cat === 'B09001') target.type = 'corporate';
  else if (cat === 'B09002') target.type = 'individual';
  // ⚠ BO 자동 ON 제거 — 엔티티 등록 BO 는 청약자 "본인"의 수익소유자가 아닐 수 있어
  //   무관한 청약자에 잘못 바인딩/신탁 ON 됨. BO 는 Trustee 스위치 ON + ✨ 로 명시 선택할 때만 채운다.
}

/** BO 직접 선택(✨) — 선택 contact 를 allottee[i] 의 bo_* 에 바인딩 + 신탁 공개 ON. */
function applyDetailToBo(index: number, detail: any) {
  const n = normalizePersonDetail(detail);
  const target = allottees.value[index];
  if (!target) return;
  if (n.name) target.boFullName = n.name;
  if (n.nricNo) target.boRegistrationNo = n.nricNo;
  if (n.address) target.boRegisteredAddress = n.address;
  const biz = (n.addresses || []).find((a: any) => String(a?.type).toLowerCase() === 'business');
  if (biz?.value) target.boBusinessAddress = String(biz.value);
  if (n.nationality) target.boPlace = n.nationality;
  if (n.occupation) target.boNature = n.occupation;
  target.isTrustee = true;
}

watch(
  () => props.selectedPerson,
  async (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx) return;

    // 풀 detail fetch — minimal contactCd/name 에서 license_number / SSM cert / 주소 등 받아옴.
    let detail: any = person;
    const cc = String((person as any).contactCd || (person as any).CONTACT_CD || '').trim();
    if (cc) {
      try {
        const { apiClient } = await import('@/shared/api/client');
        const res: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}`);
        const fetched = res?.data?.contact || res?.contact || res?.data || res;
        if (fetched && (fetched.FIRST_NAME || fetched.firstName || fetched.CONTACT_NAME || fetched.fullName)) {
          detail = fetched;
        }
      } catch (err) {
        console.warn('[AllotmentShares] selectedPerson detail fetch failed:', err);
      }
    }

    // 주소 보강 — /Contact 의 주소는 street 누락 등 truncated 케이스가 있어(normalizePersonDetail flatten 시 잘림),
    //   챗봇과 동일한 full 주소(getContactDetail → L_CONTACT_ADDRESS/L_CONTACT_BRANCH, addresses[])로 덮어쓴다.
    if (cc) {
      try {
        const ecd = entityCd.value || '';
        if (ecd) {
          const detRes: any = await docGenApi.getContactDetail(ecd, cc);
          const det = detRes?.data || detRes;
          const addrs = Array.isArray(det?.addresses) ? det.addresses : [];
          if (addrs.length > 0) detail = { ...detail, addresses: addrs };
        }
      } catch (e) {
        console.warn('[AllotmentShares] getContactDetail addresses fetch failed:', e);
      }
    }

    // fieldGroup 별 디스패치
    if (ctx.fieldGroup === 'cosec') {
      applyDetailToCosec(detail);
    } else if (ctx.fieldGroup === 'director') {
      applyDetailToDirector(ctx.index ?? 0, detail);
    } else if (ctx.fieldGroup === 'allottee' || ctx.fieldGroup === 'subscriber') {
      applyDetailToAllottee(ctx.index ?? 0, detail);
    } else if (ctx.fieldGroup === 'bo') {
      applyDetailToBo(ctx.index ?? 0, detail);
    }
    // lodger 는 Phase 2 에서 별도 처리
  },
  { deep: true },
);

// ─── Row helpers ───
function addAllottee() {
  allottees.value.push({ contactCd: '', name: '', address: '', shares: 0, shareType: 'Ordinary' });
}
function removeAllottee(i: number) { allottees.value.splice(i, 1); }
function addDirector() {
  directors.value.push({ contactCd: '', name: '', nricNo: '', dateOfAppointment: '' });
}
function removeDirector(i: number) { directors.value.splice(i, 1); }

function requestLookup(ctx: PersonLookupContext) {
  emit('request-person-lookup', ctx);
}

// ─── Drag & Drop overlay (AGM 패턴) ───
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }
function handleFieldDrop(event: DragEvent, setter: (v: string) => void) {
  isDragging.value = false;
  const json = event.dataTransfer?.getData('application/x-entity-field');
  if (json) {
    try { const { value } = JSON.parse(json); if (value) { setter(String(value)); return; } } catch { /* noop */ }
  }
  const text = event.dataTransfer?.getData('text/plain');
  if (text) setter(String(text));
}
onMounted(() => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

// ─── Task Config / processSteps (handleCreateTask 시 taskFlow 결정) ───
const taskConfigSteps = ref<string[]>([]);
onMounted(async () => {
  const taskCode = (props.selection as any)?.task?.TASK_CODE;
  if (taskCode) {
    try {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch (err) {
      console.warn('[AllotmentShares] getTaskConfigByCode failed:', err);
    }
  }
});

// ─── Create Task (Phase 1 — skeleton: 변수만 빌드 후 console + alert) ───
const isFormValid = computed(
  () => !!entityCd.value && !!companyName.value && allottees.value.some((a) => a.name && a.shares > 0),
);

async function handleCreateTask() {
  if (creating.value) return;
  // ── 검증 ──
  if (!props.taskTitle?.trim()) {
    progress.value = 'Error: Task Title is required — enter it in the chat.';
    return;
  }
  if (!entityCd.value) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  if (!isFormValid.value) {
    progress.value = 'Error: Please enter at least one allottee with name and shares.';
    return;
  }
  const taskTypeCd = (props.selection.task as any)?.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';
  try {
    // 1) Task 생성 — processSteps 로 taskFlow 결정 (incorporation post 패턴)
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    // 레지스터 등록용 shareholders 구조 — 비-preset SharesAllotmentForm 과 동일 포맷.
    //   Task List 레지스터 아이콘(registerToRegisterStore) 이 TASK_DATA.formData.shareholders 를 읽어 등록.
    //   MY currency 는 MYR 고정(B08114). class 는 코드(A20001/2/3) 로 변환.
    const classCode = (shareType?: string): string => {
      const n = String(shareType || '').toLowerCase();
      if (n.includes('pref')) return 'A20002';
      if (n.includes('other')) return 'A20003';
      return 'A20001'; // Ordinary 기본
    };
    const registerShareholders = allottees.value
      .filter((a) => a.name?.trim() && Number(a.shares) > 0)
      .map((a) => {
        const num = Number(a.shares) || 0;
        const amount = round2(num * (Number(sharePrice.value) || 0));
        return {
          shareholder: a.contactCd || '',
          shareholderName: a.name,
          currencies: [{
            currency: 'B08114',            // MYR (Ringgit Malaysia) — MY 고정
            currencyName: 'Ringgit Malaysia',
            sharesPayable: '',
            shareClasses: [{
              classOfShares: classCode(a.shareType),
              classOfSharesName: a.shareType || 'Ordinary',
              numberOfShares: String(num),
              issuedShareCapital: String(amount),
              paidUpShareCapital: String(amount),
              subClasses: [] as Array<{ name: string; numberOfShares: string }>,
            }],
          }],
        };
      });

    // officerData → TASK_DATA.formData 로 저장. shareholders(레지스터용) + presetForm(Edit 복원 스냅샷).
    const officerData = {
      entity: entityCd.value || '',
      entityName: companyName.value || '',
      entityRegNo: companyRegistrationNo.value || '',
      transactionDate: allotmentEffective.value || letterDate.value || '',
      shareholders: registerShareholders,
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      // 제목 갱신 (선택)
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: (props.selection.task as any)?.TASK_NAME,
          taskTitle: props.taskTitle,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Building documents...`;
    }

    // 2) 변수 합성
    const variables = buildAllotmentSharesVariables({
      companyName: companyName.value,
      companyRegistrationNo: companyRegistrationNo.value,
      companyAddress: companyAddress.value,
      constitutionClauseNo: constitutionClauseNo.value,
      hasConstitution: hasConstitution.value,
      // DCR 거버닝-문서 모델 + director/member 수 + 공인
      governingDocumentType: governingDocumentType.value,
      constitutionClauseNumber: constitutionClauseNo.value,
      directorCount: directorCount.value,
      memberCount: memberCount.value,
      hasCommonSeal: hasCommonSeal.value,
      cosec: cosec.value,
      directors: directors.value,
      allottees: allottees.value,
      subscribers: subscribers.value,
      lodger: lodger.value,
      sharePrice: Number(sharePrice.value) || 0,
      totalNewShares: Number(totalNewShares.value) || 0,
      totalAmount: Number(totalAmount.value) || 0,
      totalAmountWords: amountInWords.value,
      paidUpCapitalBefore: Number(paidUpCapitalBefore.value) || 0,
      paidUpCapitalAfter: Number(paidUpCapitalAfter.value) || 0,
      resolutionDate: resolutionEffective.value,
      dcrDate: dcrDate.value,
      circulationDate: circulationDate.value,
      deadlineDate: deadlineDate.value,
      letterDate: letterDate.value,
      allotmentDate: allotmentEffective.value,
      // skip 시 빈칸(나중에 입력), 아니면 입력값 또는 배정일 기본.
      periodStartDate: isFieldSkipped('periodStartDate') ? '' : (periodStartDate.value || allotmentEffective.value),
      periodEndDate: isFieldSkipped('periodEndDate') ? '' : (periodEndDate.value || allotmentEffective.value),
      signatureDate: signatureDate.value,
      eventDate: eventDate.value,
      grantOfRights: grantOfRights.value,
      hasConditions: hasConditions.value,
      conditionsText: conditionsText.value,
      multipleMembers: multipleMembers.value,
      currentIssuedShares: Number(currentIssuedShares.value) || 0,
      submissionReference: submissionReference.value,
    });
    // 3) 문서별 사이너 결정 (Variable Map T-Sign 그룹 기준)
    const directorSigners = directors.value.filter((d) => d.name?.trim())
      .map((d) => ({ name: d.name.trim(), email: String(d.email || '').trim(), role: 'signer' }));
    const memberSigners = allottees.value.filter((a) => a.name?.trim())
      .map((a) => ({ name: a.name.trim(), email: '', role: 'signer' }));
    const subscriberSigners = subscribers.value.filter((s) => s.name?.trim())
      .map((s) => ({ name: s.name.trim(), email: '', role: 'signer' }));
    const cosecSigners = cosec.value.name?.trim()
      ? [{ name: cosec.value.name.trim(), email: String(cosec.value.email || '').trim(), role: 'signer' }]
      : [];
    /** 문서명 → 자동 사이너 (매칭 안 되면 undefined → T-Sign 에서 수동 지정). 순서 중요. */
    const pickSigners = (tName: string) => {
      const n = String(tName || '').toLowerCase();
      if (/section\s*78|return\s*of\s*allotment/.test(n)) return undefined;                  // 11: 텍스트 서명 (Signers 미사용)
      if (/authority\s*to\s*issue|issue\s*shares/.test(n)) return [...directorSigners, ...memberSigners]; // 1: 이사+주주
      if (/subscription/.test(n)) return subscriberSigners.length ? subscriberSigners : undefined;        // 2/3: 청약자
      if (/waiver|preemption/.test(n)) return memberSigners.length ? memberSigners : undefined;           // 4: 기존 주주
      if (/(section\s*76|s\.?\s*76).*(secretary|lodger)|roc.*cover|cover\s*letter/.test(n))               // 9/10: 비서
        return cosecSigners.length ? cosecSigners : undefined;
      if (/pricing|letter\s*of\s*allotment|increase.*paid|paid[\s\-_]*up|section\s*76|s\.?\s*76/.test(n))  // 5/6/7/8: 이사
        return directorSigners.length ? directorSigners : undefined;
      return undefined;
    };

    // 4) Subscription 문서별 청약자 주입 — Individual 문서엔 개인 청약자, Corporate 문서엔 법인 청약자.
    //   mapper 는 subscribers[0] 만 평탄화하므로, 여기서 유형에 맞는 청약자로 subscriber_* 를 override.
    const subscriberVars = (s: SubscriberInput) => ({
      subscriber_name: s.name || '',
      subscriber_id: s.id || '',
      subscriber_dob: s.dob || '',
      subscriber_address: s.address || '',
      subscriber_nationality: s.nationality || '',
      subscriber_race: s.race || '',
      subscriber_occupation: s.occupation || '',
      subscriber_type: s.type || '',
      shares_offered: String(s.sharesOffered || ''),
      shares_subscribed: String(s.sharesSubscribed || ''),
      shares_accepted: String(s.sharesAccepted || ''),
      bo_full_name: s.boFullName || '',
      bo_registration_no: s.boRegistrationNo || '',
      bo_nature: s.boNature || '',
      bo_place: s.boPlace || '',
      bo_registered_address: s.boRegisteredAddress || '',
      bo_business_address: s.boBusinessAddress || '',
      is_politician: s.isPolitician ? 'true' : '',
      politician_details: s.politicianDetails || '',
      is_pep_connected: s.isPepConnected ? 'true' : '',
      pep_connection_details: s.pepConnectionDetails || '',
      holds_govt_position: s.holdsGovtPosition ? 'true' : '',
      govt_position_details: s.govtPositionDetails || '',
      is_trustee: s.isTrustee ? 'true' : '',   // Individual(2): [[if is_trustee]]
      has_bo: s.isTrustee ? 'true' : '',       // Corporate(3): [[if has_bo]] / [[if !has_bo]]
      appendix_ref: s.appendixRef || '',
    });
    const indivSubs = subscribers.value.filter((s) => s.type === 'individual' && s.name?.trim());
    const corpSubs = subscribers.value.filter((s) => s.type === 'corporate' && s.name?.trim());

    // 5) 문서 저장 목록 (skip 제외)
    //   ⚠ Subscription(2/3) 은 청약자별 1장 — 유형(개인/법인)에 해당하는 청약자 수만큼 N장 복제.
    //     (Incorporation Section 236(3) 패턴). 각 장은 그 청약자의 subscriber_*/bo_*/PEP + 본인 서명.
    const docsToSave = (props.selection.documents || [])
      .filter((d: any) => d?.tmpId)
      .filter((d: any) => {
        const r = isDocSkipped(d?.templateName || '');
        if (r.skipped) { console.info('[AllotmentShares] Skipping doc:', d?.templateName, '—', r.reason); return false; }
        return true;
      })
      .flatMap<any>((d: any) => {
        const tName = String(d.templateName || '').toLowerCase();

        // Subscription 문서 — 유형별 청약자 수만큼 복제.
        if (/subscription/.test(tName)) {
          const isCorp = /corporate|company|body/.test(tName);
          const targets = isCorp ? corpSubs : indivSubs;
          const baseLabel = String(d.templateName || '').replace(/\.docx?$/i, '');
          // 매칭 청약자 없으면 빈 데이터로 1장 (isDocSkipped 가 통상 걸러주지만 안전망).
          if (targets.length === 0) {
            return [{
              templateId: d.tmpId, label: d.templateName, personName: companyName.value || '',
              formData: { ...variables }, entityCd: entityCd.value || undefined, signers: undefined,
            }];
          }
          return targets.map((s) => ({
            templateId: d.tmpId,
            // 2명 이상이면 라벨에 청약자명 추가 — 리스트/미리보기에서 구분.
            label: targets.length > 1 ? `${baseLabel} — ${s.name}` : d.templateName,
            personName: s.name || companyName.value || '',
            formData: { ...variables, ...subscriberVars(s) },
            entityCd: entityCd.value || undefined,
            signers: s.name?.trim() ? [{ name: s.name.trim(), email: '', role: 'signer' }] : undefined,
          }));
        }

        // 그 외 문서 — 1장.
        return [{
          templateId: d.tmpId,
          label: d.templateName,
          personName: companyName.value || '',
          formData: { ...variables },
          entityCd: entityCd.value || undefined,
          signers: pickSigners(tName) || undefined,
        }];
      });

    if (docsToSave.length > 0) {
      progress.value = `Saving ${docsToSave.length} document(s)...`;
      try {
        // 범용 문서 저장 라우트 재사용 (taskId + documents[] → 변수치환 + L_DOC_CUST_SAVE)
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          // Edit 모드: replaceExisting → 기존 task 문서 삭제 후 재생성 (사이너 보존). create 모드: 신규 INSERT.
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
      } catch (err) {
        console.warn('[AllotmentShares] Document save failed:', err);
      }
    }

    // 6) document-status 업데이트
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      });
    } catch { /* ignore */ }

    // 7) PreSet 사용 로그
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ presetTaskId: (props.selection.task as any)?.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    // 8) 다음 페이지 prefetch
    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: props.taskTitle });
    // Create/Update 모두 동일하게 Detail 페이지로 이동.
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[AllotmentShares] create/update task failed:', err);
    progress.value = `Error: ${err?.message || 'Failed to save task.'}`;
  } finally {
    creating.value = false;
  }
}

const effectiveTaskTitle = computed(() => props.taskTitle || '');

function docLabel(d: any): string {
  return String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '');
}

/** 문서별 skip 판단 — Subscription(개인/법인)은 해당 유형 청약자가 없으면 생성 제외. */
function isDocSkipped(templateName: string): { skipped: boolean; reason?: string } {
  const n = String(templateName || '').toLowerCase();
  const hasIndiv = subscribers.value.some((s) => s.type === 'individual' && s.name?.trim());
  const hasCorp = subscribers.value.some((s) => s.type === 'corporate' && s.name?.trim());
  if (/subscription/.test(n) && /individual|natural/.test(n) && !hasIndiv) {
    return { skipped: true, reason: 'No individual subscriber' };
  }
  if (/subscription/.test(n) && /corporate|company|body/.test(n) && !hasCorp) {
    return { skipped: true, reason: 'No corporate subscriber' };
  }
  return { skipped: false };
}
const skippedDocsCount = computed(() =>
  (props.selection.documents || []).filter((d: any) => isDocSkipped(d?.templateName || '').skipped).length,
);

/** 문서가 몇 장 생성될지 — Subscription(2/3)은 유형별 청약자 수만큼 N장, 그 외 1장. */
function docCopyCount(templateName: string): number {
  const n = String(templateName || '').toLowerCase();
  if (/subscription/.test(n)) {
    const isCorp = /corporate|company|body/.test(n);
    return subscribers.value.filter(
      (s) => s.type === (isCorp ? 'corporate' : 'individual') && s.name?.trim(),
    ).length;
  }
  return 1;
}
/** skip 제외 후 실제 생성 문서 총 장수 (N장 복제 반영). */
const totalGeneratedDocs = computed(() =>
  (props.selection.documents || []).reduce((sum, d: any) => {
    if (isDocSkipped(d?.templateName || '').skipped) return sum;
    return sum + Math.max(1, docCopyCount(d?.templateName || ''));
  }, 0),
);

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
//   파생 날짜 override 는 resolution/allotment 에서 재계산되므로 미포함(v1).
function gatherState(): Record<string, any> {
  return {
    companyName: companyName.value,
    companyRegistrationNo: companyRegistrationNo.value,
    companyAddress: companyAddress.value,
    constitutionClauseNo: constitutionClauseNo.value,
    hasConstitution: hasConstitution.value,
    governingDocumentType: governingDocumentType.value,
    articleNo: articleNo.value,
    entityClauseRegistered: entityClauseRegistered.value,
    memberCount: memberCount.value,
    hasCommonSeal: hasCommonSeal.value,
    cosec: { ...cosec.value },
    directors: JSON.parse(JSON.stringify(directors.value || [])),
    allottees: JSON.parse(JSON.stringify(allottees.value || [])),
    lodger: { ...lodger.value },
    sharePrice: sharePrice.value,
    paidUpCapitalBefore: paidUpCapitalBefore.value,
    currentIssuedShares: currentIssuedShares.value,
    resolutionDate: resolutionDate.value,
    allotmentDate: allotmentDate.value,
    periodStartDate: periodStartDate.value,
    periodEndDate: periodEndDate.value,
    grantOfRights: grantOfRights.value,
    hasConditions: hasConditions.value,
    conditionsText: conditionsText.value,
    submissionReference: submissionReference.value,
    skippedFields: Array.from(skippedFields.value),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.companyName != null) companyName.value = s.companyName;
  if (s.companyRegistrationNo != null) companyRegistrationNo.value = s.companyRegistrationNo;
  if (s.companyAddress != null) companyAddress.value = s.companyAddress;
  // Governing Document — entity 등록 정관이 있으면(잠금) entity 값이 우선이라 저장값으로 덮어쓰지 않음.
  //   (entity fetch 가 async 라 restore 가 먼저 돌면 stale 값이 잠깐 들어가도, fetch 완료 시 applyRegisteredClause 가 덮어씀.)
  if (!entityClauseRegistered.value) {
    if (s.constitutionClauseNo != null) constitutionClauseNo.value = s.constitutionClauseNo;
    if (s.governingDocumentType) governingDocumentType.value = s.governingDocumentType;
  }
  if (typeof s.hasConstitution === 'boolean') hasConstitution.value = s.hasConstitution;
  if (s.memberCount) { memberCount.value = s.memberCount; memberCountManual.value = true; }
  if (typeof s.hasCommonSeal === 'boolean') hasCommonSeal.value = s.hasCommonSeal;
  if (s.cosec && typeof s.cosec === 'object') cosec.value = { ...cosec.value, ...s.cosec };
  if (Array.isArray(s.directors)) directors.value = s.directors;
  if (Array.isArray(s.allottees)) allottees.value = s.allottees;
  if (s.lodger && typeof s.lodger === 'object') lodger.value = { ...s.lodger };
  if (s.sharePrice != null) sharePrice.value = s.sharePrice;
  if (s.paidUpCapitalBefore != null) paidUpCapitalBefore.value = s.paidUpCapitalBefore;
  if (s.currentIssuedShares != null) currentIssuedShares.value = Number(s.currentIssuedShares) || 0;
  if (s.resolutionDate != null) resolutionDate.value = s.resolutionDate;
  if (s.allotmentDate != null) allotmentDate.value = s.allotmentDate;
  if (s.periodStartDate != null) periodStartDate.value = s.periodStartDate;
  if (s.periodEndDate != null) periodEndDate.value = s.periodEndDate;
  if (typeof s.grantOfRights === 'boolean') grantOfRights.value = s.grantOfRights;
  if (typeof s.hasConditions === 'boolean') hasConditions.value = s.hasConditions;
  if (s.conditionsText != null) conditionsText.value = s.conditionsText;
  if (s.submissionReference != null) submissionReference.value = s.submissionReference;
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  // 파생 watcher(share-sync 등)가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="co-panel">
    <!-- Header 제거 — wrapper(PreSetDocGenView) 가 이미 "New Task — TASK_NAME" 헤더 그림.
         panel 안에서 중복 표시 안 함. Back 버튼은 footer 의 [Back] 으로 단일화. -->

    <div class="co-content">
      <!-- Task Title 박스 -->
      <div class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:tag" width="14" />
          <span class="ml-1">Task Title</span>
        </div>
        <div class="d-flex align-center ga-2 pa-3">
          <div class="co-readonly-box flex-grow-1" :class="{ 'co-readonly-empty': !effectiveTaskTitle }">
            {{ effectiveTaskTitle || 'Not entered yet' }}
          </div>
          <v-btn variant="tonal" size="small" class="co-change-btn" @click="emit('change-task-title')">
            Change
          </v-btn>
        </div>
      </div>

      <!-- Entity 카드 -->
      <div v-if="entityData" class="co-entity-card">
        <div class="d-flex align-center ga-3">
          <div class="co-entity-icon">
            <Icon icon="mdi:domain" width="22" color="#1976d2" />
          </div>
          <div class="flex-grow-1">
            <div class="co-entity-name">{{ entityName || 'Entity' }}</div>
            <div v-if="regNo" class="co-entity-reg">Registration No: {{ regNo }}</div>
            <div v-if="articleNo || constitutionClauseNo" class="co-entity-reg" :title="articleNo">
              Constitution Clause: <strong>{{ constitutionClauseNo || '—' }}</strong>
              <span v-if="articleNo" class="co-entity-article">({{ articleNo }})</span>
            </div>
          </div>
          <v-btn variant="text" size="small" class="co-change-btn" @click="emit('change-entity')">Change</v-btn>
        </div>
      </div>
      <div v-else class="co-empty">
        <v-icon size="36" color="grey-lighten-1" class="mb-2">mdi-arrow-up-circle-outline</v-icon>
        <div class="text-body-2 font-weight-medium">Please select an Entity in the chat to begin.</div>
        <div class="text-caption text-medium-emphasis">Document forms will appear after entity is selected.</div>
      </div>

      <!-- Entity prefill 로딩 -->
      <div v-if="entityLoading" class="d-flex align-center justify-center pa-6">
        <v-progress-circular indeterminate size="36" width="3" color="primary" class="mr-3" />
        <span class="text-caption text-medium-emphasis">Loading entity data — addresses / officers / members…</span>
      </div>

      <!-- 폼 (taskTitle + entity + prefill 완료 시) -->
      <template v-else-if="formReady">

        <!-- A. Company Info -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:office-building" width="14" /><span class="ml-1">Company Information</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="companyName" label="Company Name *" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="companyRegistrationNo" label="Registration No *" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12">
              <div class="co-drop-wrap">
                <v-text-field v-model="companyAddress" label="Company Address" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => companyAddress = v)" />
              </div>
            </v-col>
            <!-- 거버닝-문서 분기 (DCR resolution_basis_*) — Constitution / Articles / Third Schedule.
                 Entity 에 정관/결의 근거 등록 시 라디오 + clause 잠금(readonly). -->
            <v-col cols="12">
              <div class="text-caption text-grey-darken-1 mb-1 d-flex align-center">
                <span>Governing Document (Resolution Basis)</span>
                <span v-if="governingDocLocked" class="co-gov-locked ml-2">
                  <v-icon size="13">mdi-lock-outline</v-icon> registered
                </span>
              </div>
              <v-radio-group v-model="governingDocumentType" @update:model-value="constitutionClauseNo = ''" :disabled="governingDocLocked" inline density="compact" hide-details>
                <v-radio label="Constitution" value="constitution" />
                <v-radio label="Articles of Association" value="articles" />
                <v-radio label="Third Schedule (Companies Act 2016)" value="third_schedule" />
              </v-radio-group>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="constitutionClauseModel"
                :items="filteredClauseOptions"
                :label="(governingDocumentType === 'articles' ? 'Article No.' : 'Constitution Clause No.') + (governingDocLocked ? ' (registered)' : '')"
                :placeholder="governingDocumentType === 'third_schedule' ? 'N/A for Third Schedule' : 'Select or type clause number'"
                :disabled="governingDocumentType === 'third_schedule'"
                :readonly="governingDocLocked"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :clearable="!governingDocLocked"
                :hint="
                  governingDocLocked
                    ? 'Registered with the entity — read-only'
                    : (governingDocumentType === 'third_schedule'
                      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
                      : (filteredClauseOptions.length > 0
                        ? `${filteredClauseOptions.length} option(s) — only the clause number is stored`
                        : 'No constitution clauses found — type manually'))
                "
                persistent-hint
              />
            </v-col>
            <!-- 멤버 수 (Authority to Issue Shares MWR) — is_sole_member / is_multiple_member.
                 라벨은 상단, 라디오 라인은 컬럼 맨 아래(combobox 힌트 라인 높이)로 정렬. -->
            <v-col cols="12" md="6" class="d-flex flex-column">
              <div class="text-caption text-grey-darken-1 mb-1">
                Members (for Members' Written Resolution)
                <span v-if="!memberCountManual" class="text-grey">— auto</span>
              </div>
              <v-radio-group :model-value="memberCount" inline density="compact" hide-details
                class="mt-auto"
                @update:model-value="onMemberCountChange">
                <v-radio label="Sole Member" value="single" />
                <v-radio label="Multiple Members" value="multiple" />
              </v-radio-group>
            </v-col>
          </v-row>
        </div>

        <!-- B. Company Secretary -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:account-tie" width="14" /><span class="ml-1">Company Secretary</span>
            <span class="co-section-docs-label ml-3">Applies to: Authority / Pricing / Letter of Allotment / ROC Cover / Section 76 Secretary</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="cosec.name" label="Secretary Name" variant="outlined" density="compact" hide-details>
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'cosec', defaultRole: 'SECRETARY' })" />
                    </template>
                    <span>Lookup person from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="cosec.licenseNo" label="License / Membership No." variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="cosec.ssmCertNo" label="SSM Practising Certificate No." variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="cosec.phone" label="Phone" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12">
              <div class="co-drop-wrap">
                <v-text-field v-model="cosec.address" label="Address" variant="outlined" density="compact" hide-details />
                <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => cosec.address = v)" />
              </div>
              <!-- 주소 후보 chip — chat 에서 선택한 contact 의 residential / service / business / branch. -->
              <!--   service 가 첫 매칭이면 기본 선택. 사용자가 다른 chip 클릭 시 즉시 교체. -->
              <div v-if="(cosec.addresses?.length ?? 0) >= 1" class="co-addr-suggest mt-1">
                <v-chip
                  v-for="(addr, ai) in cosec.addresses"
                  :key="`cosec-addr-${ai}`"
                  size="x-small"
                  variant="tonal"
                  :color="cosec.addressSelectedIdx === ai ? 'primary' : 'default'"
                  @click="() => { cosec.address = addr.value; cosec.addressSelectedIdx = ai; }"
                >
                  {{ addressSuggestionLabel(addr, ai, cosec.addresses) }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- C. Directors -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:briefcase-account" width="14" /><span class="ml-1">Directors</span>
            <span class="co-section-docs-label ml-3">Applies to: DCR / Pricing / Letter of Allotment / Increase Paid-Up / Section 76 Director / Section 78 Return</span>
            <v-spacer />
            <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" class="mr-2" @click="addDirector">Add Director</v-btn>
          </div>
          <div v-if="directors.length === 0" class="text-caption text-medium-emphasis px-3 pb-2">No directors yet — add at least one.</div>
          <v-row v-for="(d, i) in directors" :key="`dir-${i}`" dense align="center">
            <v-col cols="12" md="5">
              <v-text-field v-model="d.name" :label="`Director ${i + 1} Name`" variant="outlined" density="compact" hide-details>
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'director', index: i, defaultRole: 'DIRECTOR' })" />
                    </template>
                    <span>Lookup director from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="d.nricNo" label="NRIC / ID No." variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="d.email" label="Email" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="1" class="d-flex justify-end">
              <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeDirector(i)" />
            </v-col>
          </v-row>
        </div>

        <!-- D. Allottees = Subscribers (동일인 — 한 곳에서 입력) -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:cash-multiple" width="14" /><span class="ml-1">Allottees / Subscribers</span>
            <span class="co-section-docs-label ml-3">Same person — Waiver / Letter of Allotment / Increase Paid-Up / Section 78 + Subscription (2/3)</span>
            <v-spacer />
            <v-btn size="x-small" variant="tonal" prepend-icon="mdi-plus" class="mr-2" @click="addAllottee">Add</v-btn>
          </div>
          <div v-if="allottees.length === 0" class="text-caption text-medium-emphasis px-3 pb-2">No allottees yet — add at least one.</div>

          <div v-for="(a, i) in allottees" :key="`al-${i}`" class="co-subscriber-card">
            <v-row dense align="center">
              <v-col cols="12" md="5">
                <v-text-field v-model="a.name" :label="`${i + 1}. Name`" variant="outlined" density="compact" hide-details>
                  <template #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="requestLookup({ fieldGroup: 'allottee', index: i, defaultRole: null })" />
                      </template>
                      <span>Lookup contact — fills allotment + subscription (Individual/Corporate auto)</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="a.type"
                  :items="[{ title: 'Individual', value: 'individual' }, { title: 'Corporate', value: 'corporate' }]"
                  label="Type" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model.number="a.shares" label="Shares" type="number" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="a.shareType" label="Class" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="1" class="d-flex justify-end">
                <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeAllottee(i)" />
              </v-col>
            </v-row>

            <!-- 청약 상세 (동일인) — Subscription 문서가 패키지에 있을 때만. 항상 펼침. -->
            <div v-if="docFlags.hasAnySubscription" class="co-sub-open mt-2">
              <div class="co-sub-open-title">
                <Icon icon="mdi:file-document-edit" width="13" class="mr-1" />
                Subscription details ({{ a.type === 'corporate' ? 'Corporate' : 'Individual' }})
              </div>
              <div class="co-sub-open-body">
                  <v-row dense>
                    <v-col cols="12" md="4"><v-text-field v-model="a.idNo" :label="a.type === 'corporate' ? 'Company No.' : 'I/C / Passport No.'" variant="outlined" density="compact" hide-details /></v-col>
                    <v-col cols="12" md="4">
                      <PreSetDateField v-model="a.dob" :label="a.type === 'corporate' ? 'Date of Incorporation' : 'Date of Birth'" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-combobox v-model="a.nationality" :items="NATIONALITY_ITEMS"
                        :label="a.type === 'corporate' ? 'Place of Incorporation' : 'Nationality'"
                        variant="outlined" density="compact" hide-details auto-select-first clearable />
                    </v-col>
                    <v-col cols="12">
                      <div class="co-drop-wrap">
                        <v-text-field v-model="a.address" label="Address" variant="outlined" density="compact" hide-details />
                        <div v-if="isDragging" class="co-drop-overlay" @dragover.prevent @drop.prevent="handleFieldDrop($event, (v) => a.address = v)" />
                      </div>
                    </v-col>
                    <v-col v-if="a.type !== 'corporate'" cols="12" md="6">
                      <v-combobox v-model="a.occupation" :items="OCCUPATION_ITEMS" label="Occupation"
                        variant="outlined" density="compact" hide-details auto-select-first clearable />
                    </v-col>
                    <v-col v-if="a.type !== 'corporate'" cols="12" md="6"><v-text-field v-model="a.race" label="Race" variant="outlined" density="compact" hide-details /></v-col>
                  </v-row>

                  <div class="co-subscriber-sub">
                    <div class="co-subscriber-sub-title">Shares <span class="co-auto-hint">— auto from Allotted Shares, editable</span></div>
                    <v-row dense>
                      <v-col cols="12" md="3"><v-text-field v-model.number="a.sharesOffered" label="Shares Offered" type="number" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="3"><v-text-field v-model.number="a.sharesSubscribed" label="Shares Subscribed" type="number" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="3"><v-text-field v-model.number="a.sharesAccepted" label="Shares Accepted" type="number" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="3"><v-text-field v-model="a.appendixRef" label="Appendix Ref" variant="outlined" density="compact" hide-details /></v-col>
                    </v-row>
                  </div>

                  <div class="co-subscriber-sub">
                    <div class="co-subscriber-sub-title">PEP Declarations</div>
                    <v-row dense align="center">
                      <v-col cols="12" md="4">
                        <v-checkbox v-model="a.isPolitician" density="compact" hide-details>
                          <template #label>
                            <span class="mr-1">Is a politician</span>
                            <v-tooltip location="top" max-width="320">
                              <template #activator="{ props: tp }">
                                <v-icon v-bind="tp" size="15" color="error" style="cursor:help">mdi-help-circle-outline</v-icon>
                              </template>
                              <div class="co-pep-tip">
                                <div>{{ PEP_GUIDE[0].desc }}</div>
                                <div class="co-pep-tip-ex">Examples:</div>
                                <ul><li v-for="ex in PEP_GUIDE[0].examples" :key="ex">{{ ex }}</li></ul>
                              </div>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" md="8"><v-text-field v-if="a.isPolitician" v-model="a.politicianDetails" label="Politician details" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="4">
                        <v-checkbox v-model="a.isPepConnected" density="compact" hide-details>
                          <template #label>
                            <span class="mr-1">Connected to a politician</span>
                            <v-tooltip location="top" max-width="320">
                              <template #activator="{ props: tp }">
                                <v-icon v-bind="tp" size="15" color="error" style="cursor:help">mdi-help-circle-outline</v-icon>
                              </template>
                              <div class="co-pep-tip">
                                <div>{{ PEP_GUIDE[1].desc }}</div>
                                <div class="co-pep-tip-ex">Examples:</div>
                                <ul><li v-for="ex in PEP_GUIDE[1].examples" :key="ex">{{ ex }}</li></ul>
                              </div>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" md="8"><v-text-field v-if="a.isPepConnected" v-model="a.pepConnectionDetails" label="PEP connection details" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="4">
                        <v-checkbox v-model="a.holdsGovtPosition" density="compact" hide-details>
                          <template #label>
                            <span class="mr-1">Holds government position</span>
                            <v-tooltip location="top" max-width="320">
                              <template #activator="{ props: tp }">
                                <v-icon v-bind="tp" size="15" color="error" style="cursor:help">mdi-help-circle-outline</v-icon>
                              </template>
                              <div class="co-pep-tip">
                                <div>{{ PEP_GUIDE[2].desc }}</div>
                                <div class="co-pep-tip-ex">Examples:</div>
                                <ul><li v-for="ex in PEP_GUIDE[2].examples" :key="ex">{{ ex }}</li></ul>
                              </div>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" md="8"><v-text-field v-if="a.holdsGovtPosition" v-model="a.govtPositionDetails" label="Government position details" variant="outlined" density="compact" hide-details /></v-col>
                    </v-row>
                  </div>

                  <div class="co-subscriber-sub">
                    <div class="d-flex align-center">
                      <div class="co-subscriber-sub-title mb-0">Holding as Trustee / Nominee</div>
                      <v-switch v-model="a.isTrustee" color="success" density="compact" hide-details class="ml-3 co-bo-switch"
                        :label="a.isTrustee ? 'Yes — disclose Beneficial Owner' : 'No'" />
                    </div>
                    <v-row v-if="a.isTrustee" dense>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="a.boFullName" label="Full Name of Organisation / Individual" variant="outlined" density="compact" hide-details>
                          <template #append-inner>
                            <v-tooltip location="top" :open-delay="150">
                              <template #activator="{ props: tProps }">
                                <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                                  @click="requestLookup({ fieldGroup: 'bo', index: i, defaultRole: null })" />
                              </template>
                              <span>Lookup Beneficial Owner from chat</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" md="6"><v-text-field v-model="a.boNature" label="Nature of Business / Name of Employer" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="6"><v-text-field v-model="a.boPlace" label="Place of Registration / Place of Working" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12" md="6"><v-text-field v-model="a.boRegistrationNo" label="NRIC / Passport No." variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12"><v-text-field v-model="a.boRegisteredAddress" label="Registered Business / Residential Address" variant="outlined" density="compact" hide-details /></v-col>
                      <v-col cols="12"><v-text-field v-model="a.boBusinessAddress" label="Business / Working Place Address" variant="outlined" density="compact" hide-details /></v-col>
                    </v-row>
                  </div>
              </div>
            </div>
          </div>
        </div>

        <!-- E. Issue Information -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:chart-pie" width="14" /><span class="ml-1">Issue Information</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="3">
              <v-text-field v-model.number="sharePrice" label="Share Price (RM)" type="number" step="0.01" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field :model-value="totalNewShares" label="Total New Shares" variant="outlined" density="compact" hide-details readonly />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field :model-value="totalAmountDisplay" label="Total Amount (RM)" variant="outlined" density="compact" hide-details readonly />
            </v-col>
            <v-col cols="12">
              <v-text-field :model-value="amountInWords" label="Amount in Words" variant="outlined" density="compact" hide-details readonly
                placeholder="Auto-generated from Total Amount" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model.number="paidUpCapitalBefore" label="Paid-Up Capital Before (RM)" type="number" step="0.01" variant="outlined" density="compact" hide-details="auto"
                hint="Auto-loaded from register — editable" persistent-hint />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field :model-value="paidUpCapitalAfterDisplay" label="Paid-Up Capital After (RM)" variant="outlined" density="compact" hide-details readonly />
            </v-col>
          </v-row>
        </div>

        <!-- E2. Section 78 — Return of Allotment 합계/명세 (자동 계산, 읽기 전용). Section 78 문서가 패키지에 있을 때만. -->
        <div v-if="docFlags.hasSection78Return" class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:file-table-outline" width="14" /><span class="ml-1">Section 78 — Return of Allotment</span>
            <span class="co-section-docs-label ml-3">Auto-calculated from Allottees + Entity Register</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field :model-value="currentIssuedShares" label="Total Current Issued (shares)" variant="outlined" density="compact" hide-details readonly
                hint="From Entity Register (before this allotment)" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field :model-value="totalAdditionalShares" label="Total Additional Issued (shares)" variant="outlined" density="compact" hide-details readonly
                hint="= Σ Allottees shares (cash)" persistent-hint />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field :model-value="totalAccumulatedShares" label="Total Accumulated Issued (shares)" variant="outlined" density="compact" hide-details readonly
                hint="= Current + Additional" persistent-hint />
            </v-col>
          </v-row>

          <!-- Share Details — class 별 자동 생성 -->
          <div class="co-subscriber-sub">
            <div class="co-subscriber-sub-title">Share Details (auto from Allottees)</div>
            <div v-if="shareDetailRows.length === 0" class="text-caption text-medium-emphasis">No allottee shares entered yet.</div>
            <div v-for="(sd, si) in shareDetailRows" :key="`sd-${si}`" class="co-doc-item">
              <span class="co-doc-name" style="min-width:90px">{{ sd.type }}</span>
              <span class="text-caption text-medium-emphasis">No: <strong>{{ sd.number.toLocaleString() }}</strong></span>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">@ RM {{ sd.price }}</span>
              <v-spacer />
              <span class="text-caption">Total: <strong>RM {{ sd.totalIssued.toLocaleString() }}</strong></span>
            </div>
          </div>

          <!-- "between the [period start] and [period end]" — 여러 배정 건의 기간. 비우면 배정일로 자동 채움, 필드 안 X 로 skip(빈칸) 가능 -->
          <div class="co-subscriber-sub mt-2">
            <div class="co-subscriber-sub-title">Allotment Period (between the … and …)</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <PreSetDateField
                  v-model="periodStartDate" label="Period Start" skippable
                  :skipped="isFieldSkipped('periodStartDate')"
                  @update:skipped="(v) => toggleFieldSkip('periodStartDate', v)"
                />
                <div class="co-period-hint">Defaults to Allotment Date if left blank</div>
              </v-col>
              <v-col cols="12" md="6">
                <PreSetDateField
                  v-model="periodEndDate" label="Period End" skippable
                  :skipped="isFieldSkipped('periodEndDate')"
                  @update:skipped="(v) => toggleFieldSkip('periodEndDate', v)"
                />
                <div class="co-period-hint">Defaults to Allotment Date if left blank</div>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- F. Dates — 고객 입력 2개(Resolution / Allotment), 나머지 5개 자동 파생 -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:calendar-multiple" width="14" /><span class="ml-1">Dates</span>
            <span class="co-section-docs-label ml-3">Enter 2 dates — the rest are auto-calculated</span>
            <v-spacer />
            <v-btn size="x-small" variant="tonal" :color="allDatesSkipped ? 'primary' : 'grey'"
              :prepend-icon="allDatesSkipped ? 'mdi-restore' : 'mdi-close'"
              class="mr-2" @click="toggleAllDatesSkip">
              {{ allDatesSkipped ? 'Restore all dates' : 'Skip all dates' }}
            </v-btn>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="resolutionLocalText"
                :placeholder="resolutionDatePlaceholder"
                :label="isFieldSkipped('resolutionDate') ? 'Resolution Date' : 'Resolution Date *'"
                variant="outlined" density="compact" hide-details="auto"
                :class="{ 'co-field-skipped': isFieldSkipped('resolutionDate') }"
                hint="→ DCR · Circulation · Letter · Signature · Deadline (+28d)" persistent-hint
                @blur="resolutionOnBlur($event.target.value)"
              >
                <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn" @mousedown.prevent @click.stop="toggleFieldSkip('resolutionDate', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip — fill before lodging
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="isFieldSkipped('resolutionDate')" class="co-restore-link" @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
                  <v-menu v-else v-model="resolutionDateMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      v-bind="resolutionDatePickerProps"
                      v-model="resolutionDatePickerValue"
                      @update:model-value="resolutionDateMenu = false"
                    />
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="allotmentLocalText"
                :placeholder="allotmentDatePlaceholder"
                :label="isFieldSkipped('allotmentDate') ? 'Allotment Date' : 'Allotment Date *'"
                variant="outlined" density="compact" hide-details="auto"
                :class="{ 'co-field-skipped': isFieldSkipped('allotmentDate') }"
                hint="→ Event Date" persistent-hint
                @blur="allotmentOnBlur($event.target.value)"
              >
                <template v-if="!isFieldSkipped('allotmentDate')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn" @mousedown.prevent @click.stop="toggleFieldSkip('allotmentDate', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip — fill before lodging
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="isFieldSkipped('allotmentDate')" class="co-restore-link" @click.stop="toggleFieldSkip('allotmentDate', false)">Restore</a>
                  <v-menu v-else v-model="allotmentDateMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      v-bind="allotmentDatePickerProps"
                      v-model="allotmentDatePickerValue"
                      @update:model-value="allotmentDateMenu = false"
                    />
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- 자동 계산 — 기본은 auto, 필요 시 칸마다 직접 입력/달력으로 override 가능 (소스 skip 시 숨김) -->
          <div v-if="resolutionEffective || allotmentEffective" class="co-derived-dates mt-3">
            <div class="co-derived-title">
              <Icon icon="mdi:calculator-variant-outline" width="13" />
              <span class="ml-1">Auto-calculated</span>
              <span class="co-derived-hint ml-2">— editable; type or pick to override, leave as-is to keep auto</span>
            </div>
            <v-row dense class="mt-1">
              <v-col v-for="item in derivedDates" :key="item.key" cols="12" sm="6" md="4">
                <v-text-field
                  v-model="item.d.localText"
                  :placeholder="item.d.placeholder"
                  :label="item.label"
                  variant="outlined" density="compact" hide-details="auto"
                  :class="{ 'co-date-overridden': item.d.overridden }"
                  @blur="item.d.onBlur($event.target.value)"
                >
                  <template #append-inner>
                    <a v-if="item.d.overridden" class="co-reset-link" @mousedown.prevent @click.stop="item.d.reset()">auto</a>
                    <v-menu v-model="item.d.menu" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props: menuProps }">
                        <v-icon v-bind="menuProps" color="success" size="18" style="cursor:pointer">mdi-calendar</v-icon>
                      </template>
                      <v-date-picker
                        v-bind="item.d.pickerProps"
                        v-model="item.d.pickerValue"
                        @update:model-value="item.d.onPick()"
                      />
                    </v-menu>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
        </div>

        <!-- H. Conditions — 평이한 라벨 + 자동값. 전부 한 화면에 노출 (아코디언 없음). -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:checkbox-marked-outline" width="14" /><span class="ml-1">Conditions</span>
            <span class="co-section-docs-label ml-3">Applies to: Section 76 / Waiver</span>
          </div>
          <v-row dense align="center">
            <!-- 공인(common seal) — Increase of Paid-Up Capital 의 has_common_seal / no_common_seal -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <v-checkbox v-model="hasCommonSeal" label="Company has a common seal (Increase of Paid-Up Capital)" density="compact" hide-details />
                <v-tooltip location="top" max-width="340">
                  <template #activator="{ props: tp }">
                    <v-icon v-bind="tp" size="15" color="grey" class="ml-1" style="cursor:help">mdi-help-circle-outline</v-icon>
                  </template>
                  Tick if the company keeps an official common seal. Controls the execution block of the
                  Increase of Paid-Up Capital resolution (sealed vs unsealed signing).
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <v-checkbox v-model="hasConditions" label="Approval has special conditions (Section 76)" density="compact" hide-details />
                <v-tooltip location="top" max-width="340">
                  <template #activator="{ props: tp }">
                    <v-icon v-bind="tp" size="15" color="grey" class="ml-1" style="cursor:help">mdi-help-circle-outline</v-icon>
                  </template>
                  Tick if the members' approval for the share issue is subject to special conditions
                  (Section 76, Companies Act 2016). A box to describe the conditions will appear.
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <v-checkbox v-model="grantOfRights" label="Rights issue — grant of rights (Section 76)" density="compact" hide-details />
                <v-tooltip location="top" max-width="340">
                  <template #activator="{ props: tp }">
                    <v-icon v-bind="tp" size="15" color="grey" class="ml-1" style="cursor:help">mdi-help-circle-outline</v-icon>
                  </template>
                  Tick if this allotment is a rights issue — existing members are first granted the right
                  to subscribe in proportion to their holdings (Section 76) before new shares are offered.
                </v-tooltip>
              </div>
            </v-col>
            <!-- 특별 조건 체크 시에만 상세 텍스트 인라인 -->
            <v-col v-if="hasConditions" cols="12">
              <v-textarea v-model="conditionsText" label="Conditions — describe the special conditions" variant="outlined" density="compact" hide-details rows="2" />
            </v-col>
            <!-- Multiple Members(Waiver) — allottee 2명 이상이면 자동 체크 (읽기 전용) -->
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <v-checkbox
                  :model-value="multipleMembers"
                  readonly
                  color="success"
                  :label="`Multiple existing shareholders — Waiver (auto: ${allottees.filter((a) => a.name).length})`"
                  density="compact" hide-details
                />
                <v-tooltip location="top" max-width="340">
                  <template #activator="{ props: tp }">
                    <v-icon v-bind="tp" size="15" color="grey" class="ml-1" style="cursor:help">mdi-help-circle-outline</v-icon>
                  </template>
                  Auto-detected from the number of existing shareholders. When there are 2 or more members,
                  a Waiver of Pre-emption Rights is required and generated. Read-only.
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="submissionReference" label="Submission Reference (ROC)" variant="outlined" density="compact" hide-details>
                <template #append-inner>
                  <v-tooltip location="top" max-width="340">
                    <template #activator="{ props: tp }">
                      <v-icon v-bind="tp" size="15" color="grey" style="cursor:help">mdi-help-circle-outline</v-icon>
                    </template>
                    Optional reference number for this ROC lodgement (your internal/SSM submission ref).
                    Printed on the ROC cover letter where applicable.
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </div>

        <!-- I. 생성될 문서 리스트 (Will generate / Skipped) -->
        <div class="co-section">
          <div class="co-section-header">
            <Icon icon="mdi:file-document-multiple-outline" width="14" />
            <span class="ml-1">Documents — {{ totalGeneratedDocs }} to generate</span>
            <span class="co-section-docs-label ml-2">({{ selection.documents?.length || 0 }} templates)</span>
            <v-chip v-if="skippedDocsCount > 0" size="x-small" color="warning" variant="tonal" class="ml-2 co-chip-skip">
              {{ skippedDocsCount }} skipped
            </v-chip>
          </div>
          <div v-if="!(selection.documents?.length)" class="text-caption text-medium-emphasis px-3 pb-2">
            No documents configured for this preset task.
          </div>
          <div
            v-for="(doc, idx) in (selection.documents || [])"
            :key="(doc as any).docCode || `doc-${idx}`"
            class="co-doc-item"
            :class="{ 'co-doc-skipped': isDocSkipped((doc as any).templateName || '').skipped }"
          >
            <span class="co-doc-num">{{ idx + 1 }}</span>
            <Icon icon="mdi:file-document-outline" width="14" class="mr-2" />
            <span class="co-doc-name">{{ docLabel(doc) }}</span>
            <v-spacer />
            <v-chip
              v-if="isDocSkipped((doc as any).templateName || '').skipped"
              size="x-small" color="warning" variant="tonal" class="co-chip-skip"
            >
              Skipped — {{ isDocSkipped((doc as any).templateName || '').reason }}
            </v-chip>
            <template v-else>
              <v-chip v-if="docCopyCount((doc as any).templateName || '') > 1" size="x-small" color="indigo" variant="tonal" class="mr-1">
                × {{ docCopyCount((doc as any).templateName || '') }} copies
              </v-chip>
              <v-chip size="x-small" color="success" variant="tonal">
                <v-icon start size="12">mdi-check</v-icon>Will generate
              </v-chip>
            </template>
          </div>
        </div>

        <!-- 진행 메시지 -->
        <div v-if="progress" class="text-caption my-2" :class="progress.startsWith('Error:') ? 'text-error' : 'text-medium-emphasis'">{{ progress }}</div>
      </template>
    </div>

    <!-- Footer -->
    <div class="co-footer">
      <v-btn variant="outlined" :disabled="creating" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn color="primary" variant="flat" :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
              :loading="creating" :disabled="creating || !isFormValid || (editMode && !isDirty)"
              @click="handleCreateTask">
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
/* AGM 패턴 답습 — PreSetChangeOfficerPanel.vue 의 .co-* 와 동일 */
.co-panel { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
.co-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0;
}
.co-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.co-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.co-content { flex: 1; overflow-y: auto; padding: 20px 24px; }

.co-entity-card {
  background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px;
  padding: 16px; margin-bottom: 20px;
}
.co-entity-icon {
  width: 40px; height: 40px; border-radius: 10px; background: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.co-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.co-entity-reg { font-size: 12px; color: #666; margin-top: 1px; }
.co-gov-locked {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 11px; font-weight: 600; color: #888;
}
.co-entity-article {
  color: #999; margin-left: 4px; font-size: 11px; display: inline-block;
  max-width: 280px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  vertical-align: middle;
}
.co-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px 16px; background: #fafafa; border: 1px dashed #e0e0e0;
  border-radius: 12px; margin-bottom: 20px; text-align: center;
}
.co-readonly-box {
  background: #fafafa; border: 1px solid #eee; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5;
}
.co-readonly-empty { color: #bbb; font-style: italic; }
.co-change-btn { align-self: flex-start; }

.co-section {
  margin-bottom: 16px; background: #ffffff; border: 1px solid #e9edf5;
  border-radius: 12px; padding: 0 0 12px 0; overflow: hidden;
}
.co-section > :not(.co-section-header) {
  padding-left: 14px; padding-right: 14px;
}
.co-section-header {
  display: flex; align-items: center; gap: 6px; font-size: 12px;
  font-weight: 600; color: #283593; margin: 0 0 12px 0; padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}
.co-section-docs-label { font-size: 11px; color: #888; font-weight: 500; }

.co-footer {
  display: flex; justify-content: space-between;
  padding: 12px 24px; border-top: 1px solid #f0f0f0;
}

.co-addr-suggest {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 4px;
}

.co-derived-dates {
  background: #f6f8fd; border: 1px dashed #d6dcf2; border-radius: 8px; padding: 8px 12px;
}
.co-derived-title {
  display: flex; align-items: center; font-size: 11px; font-weight: 600;
  color: #5c6bc0; margin-bottom: 6px;
}
.co-derived-hint { font-size: 11px; color: #9aa0b4; font-weight: 400; }
.co-reset-link {
  font-size: 11px; color: #5c6bc0; cursor: pointer; margin-right: 4px; white-space: nowrap;
  text-decoration: underline; align-self: center;
}
.co-reset-link:hover { color: #3949ab; }
/* override 된 칸은 살짝 강조 — auto 가 아님을 시각적으로 표시 */
.co-date-overridden :deep(.v-field) { background: #fff8ec; }

.co-doc-item {
  display: flex; align-items: center; gap: 4px;
  padding: 7px 14px; border-top: 1px solid #f2f4f9; font-size: 13px;
}
.co-doc-item:first-of-type { border-top: none; }
.co-doc-num { width: 20px; color: #aab; font-size: 11px; text-align: right; margin-right: 6px; }
.co-doc-name { color: #333; }
.co-doc-skipped { opacity: 0.55; }
.co-doc-skipped .co-doc-name { text-decoration: line-through; }

/* Subscriber 카드 */
.co-subscriber-card {
  margin: 8px 14px 4px; padding: 10px 12px;
  border: 1px solid #e9edf5; border-radius: 10px; background: #fcfdff;
}
.co-subscriber-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.co-subscriber-title { font-size: 13px; font-weight: 600; color: #283593; }
.co-subscriber-sub { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #eef1f8; }
.co-subscriber-sub-title { font-size: 11px; font-weight: 600; color: #8a90a6; margin-bottom: 4px; }
.co-period-hint { font-size: 11px; color: rgba(0,0,0,0.55); margin-top: 2px; padding-left: 4px; }
.co-sub-expand { border: 1px solid #eef1f8; border-radius: 8px; overflow: hidden; }
.co-sub-expand :deep(.v-expansion-panel-title) { min-height: 36px; font-size: 12px; font-weight: 600; color: #5c6bc0; }
.co-sub-expand :deep(.v-expansion-panel-text__wrapper) { padding: 8px 12px 12px; }
/* 항상 펼쳐진 Subscription details */
.co-sub-open { border: 1px solid #eef1f8; border-radius: 8px; overflow: hidden; }
.co-sub-open-title {
  display: flex; align-items: center; min-height: 34px; padding: 6px 12px;
  font-size: 12px; font-weight: 600; color: #5c6bc0;
  background: linear-gradient(180deg, #f3f5fd 0%, #eef1fb 100%); border-bottom: 1px solid #eef1f8;
}
.co-sub-open-body { padding: 8px 12px 12px; }
/* PEP 도움말 툴팁 */
.co-pep-tip { font-size: 12px; line-height: 1.5; }
.co-pep-tip-ex { font-weight: 600; margin-top: 4px; }
.co-pep-tip ul { margin: 2px 0 0; padding-left: 16px; }
.co-auto-hint { font-weight: 400; color: #9aa0b4; font-size: 11px; }
/* number input 의 위아래 스피너 버튼 제거 (shares / price 등) */
:deep(input[type="number"])::-webkit-outer-spin-button,
:deep(input[type="number"])::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
:deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}
/* BO trustee 스위치 — 작게 + 라벨 폰트 축소 */
.co-bo-switch { transform: scale(0.8); transform-origin: left center; }
.co-bo-switch :deep(.v-label) { font-size: 12px; opacity: 1; }
/* warning tonal 칩의 연한 amber 글자 → 진한 갈색으로 대비 확보 */
.co-chip-skip { color: #8a5a00 !important; font-weight: 600; }
.co-chip-skip :deep(.v-chip__content) { color: #8a5a00 !important; }

/* 필드 Skip (AOD preset 패턴) */
.co-skip-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%; cursor: pointer;
  transition: background 0.15s ease; margin-right: 2px;
}
.co-skip-btn:hover { background: rgba(244, 67, 54, 0.12); }
.co-skip-btn:hover :deep(.v-icon) { color: #f44336 !important; }
.co-field-skipped :deep(.v-field) { background-color: #f5f5f5; }
.co-field-skipped :deep(.v-label) { text-decoration: line-through; opacity: 0.6; }
.co-restore-link {
  font-size: 12px; color: #1976d2; cursor: pointer; text-decoration: underline; white-space: nowrap;
}
.co-restore-link:hover { color: #0d47a1; }

.co-drop-wrap { position: relative; }
.co-drop-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 10;
  border: 2px dashed #5c6bc0; border-radius: 6px;
  background: rgba(92, 107, 192, 0.05); cursor: copy;
  transition: background 0.15s ease;
}
.co-drop-overlay:hover {
  background: rgba(92, 107, 192, 0.18); border-color: #3949ab;
}
</style>
